import moment from "moment";
import {
  abrevConcepto,
  labelConcepto,
} from "../../pagos/pagos_no_proveedor/conceptos";
import {
  ORIGEN_CHEQUE,
  camposOrigenCheque,
} from "../cheques_proveedores/cheques.constants";

/** true cuando el usuario recargó la pestaña (F5), no en navegación SPA normal */
export const esRecargaPagina = () => {
  if (typeof performance === "undefined") return false;
  const [nav] = performance.getEntriesByType("navigation");
  return nav?.type === "reload";
};

/**
 * Ciclo de vida OP:
 *   pendiente_aprobacion → generada → conciliada
 *   pendiente_aprobacion | generada → anulada
 */
export const ESTADO_OP = {
  PENDIENTE_APROBACION: "pendiente_aprobacion",
  GENERADA: "generada",
  CONCILIADA: "conciliada",
  ANULADA: "anulada",
};

export const ESTADO_OP_LABEL = {
  [ESTADO_OP.PENDIENTE_APROBACION]: "Pendiente de aprobación",
  [ESTADO_OP.GENERADA]: "Generada",
  [ESTADO_OP.CONCILIADA]: "Conciliada",
  [ESTADO_OP.ANULADA]: "Anulada",
};

export const ESTADOS_OP_FILTRO = [
  { value: ESTADO_OP.PENDIENTE_APROBACION, label: ESTADO_OP_LABEL.pendiente_aprobacion },
  { value: ESTADO_OP.GENERADA, label: ESTADO_OP_LABEL.generada },
  { value: ESTADO_OP.CONCILIADA, label: ESTADO_OP_LABEL.conciliada },
  { value: ESTADO_OP.ANULADA, label: ESTADO_OP_LABEL.anulada },
];

export const normalizarEstadoOp = (estado) =>
  String(estado || "")
    .trim()
    .toLowerCase();

export const etiquetaEstadoOp = (estado, estadoLabel = null) => {
  if (estadoLabel) return String(estadoLabel);
  const e = normalizarEstadoOp(estado);
  return ESTADO_OP_LABEL[e] ?? (estado ? String(estado) : "—");
};

export const colorEstadoOp = (estado) => {
  const e = normalizarEstadoOp(estado);
  if (e === ESTADO_OP.CONCILIADA) return "success";
  if (e === ESTADO_OP.ANULADA) return "error";
  if (e === ESTADO_OP.GENERADA) return "info";
  if (e === ESTADO_OP.PENDIENTE_APROBACION) return "warning";
  return "default";
};

export const esOpPendienteAprobacion = (estado) =>
  normalizarEstadoOp(estado) === ESTADO_OP.PENDIENTE_APROBACION;

export const esOpGenerada = (estado) =>
  normalizarEstadoOp(estado) === ESTADO_OP.GENERADA;

export const esOpConciliada = (estado) =>
  normalizarEstadoOp(estado) === ESTADO_OP.CONCILIADA;

export const esOpAnulada = (estado) =>
  normalizarEstadoOp(estado) === ESTADO_OP.ANULADA;

/** Solo pendiente de aprobación (una vez aprobada/generada ya no se anula). */
export const esOpAnulable = (estado) => {
  const e = normalizarEstadoOp(estado);
  return e === ESTADO_OP.PENDIENTE_APROBACION;
};

export const esOpEditable = (estado) => esOpPendienteAprobacion(estado);

/** Acciones de negocio deshabilitadas cuando la OP ya no es operativa. */
export const esOpAccionesOperativas = (estado) => {
  const e = normalizarEstadoOp(estado);
  return e !== ESTADO_OP.ANULADA && e !== ESTADO_OP.CONCILIADA;
};

export const mensajeErrorAnularOp = (err) => {
  const codigo = String(err?.codigo || "").toLowerCase();
  if (codigo === "op_conciliada") {
    return "No se puede anular un pago conciliado.";
  }
  if (codigo === "anticipo_con_aplicaciones") {
    return "No se puede anular un adelanto que ya fue imputado a facturas.";
  }
  return (
    err?.message ||
    err?.msg ||
    "No se pudo anular la orden de pago"
  );
};

export const formatMoneda = (valor) => {
  if (valor == null || valor === "") return "—";
  return Number(valor).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  });
};

export const formatCuit = (cuit) => {
  const d = String(cuit || "").replace(/\D/g, "");
  if (d.length !== 11) return cuit || "—";
  return `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}`;
};

export const motivoNoSeleccionableLabel = (motivo) => {
  const m = String(motivo || "").toLowerCase();
  if (m === "sin_cbu") return "Sin CBU";
  if (m === "billetera_virtual") return "Billetera virtual (CVU)";
  if (!motivo) return "No seleccionable";
  return String(motivo).replace(/_/g, " ");
};

/** Resumen del preview o de la respuesta de generar. */
export const obtenerResumenPreview = (preview) =>
  preview?.resumen_global ?? preview?.resumen ?? null;

export const obtenerResumenGeneracion = (data) =>
  data?.resumen ?? data?.resumen_global ?? null;

/** Cantidad de OPs en respuesta de generar. */
export const obtenerCantidadOrdenesGeneradas = (data) =>
  Number(
    data?.cantidad_ordenes ??
      data?.cantidad_ordenes_pago ??
      data?.ordenes_pago?.length ??
      (data?.orden_pago ? 1 : 0)
  );

/** true si el preview tiene datos de resumen listos para avanzar. */
export const previewTieneResumen = (preview) =>
  Boolean(obtenerResumenPreview(preview)?.total_a_pagar != null);

/** Grupos por proveedor/empresa; si el back envía forma plana, un solo grupo sintético. */
export const obtenerGruposPreview = (preview) => {
  if (Array.isArray(preview?.grupos) && preview.grupos.length > 0) {
    return preview.grupos;
  }
  if (!preview) return [];
  const cuit = String(
    preview.proveedor?.nro_cuit || preview.proveedor?.cuit || preview.cuit || ""
  ).replace(/\D/g, "");
  return [
    {
      cuit,
      razon_social: preview.proveedor?.razon_social || "",
      id_empresa: null,
      empresa: null,
      comprobantes: preview.comprobantes ?? [],
      retenciones: preview.retenciones ?? {},
      resumen: preview.resumen ?? {},
      proveedor: preview.proveedor,
      detalle: preview.detalle,
      advertencias: preview.advertencias ?? [],
    },
  ];
};

export const esMultiEmpresa = (preview) =>
  (preview?.grupos?.length ?? 0) > 1;

/** Clave sintética del grupo "Pagos varios" en pendientes / selección. */
export const CLAVE_PAGOS_NO_PROVEEDOR = "__pagos_no_proveedor__";

export const esGrupoPagosNoProveedor = (prov) =>
  Boolean(prov?.es_pagos_no_proveedor);

export const esPagoNoProveedor = (doc) =>
  Boolean(
    doc?.es_pago_no_proveedor ||
      doc?.es_pagos_no_proveedor ||
      (doc?.id_pago_no_proveedor != null && doc.id_pago_no_proveedor !== "")
  );

export const idPagoNoProveedor = (doc) => {
  const id = doc?.id_pago_no_proveedor ?? doc?.id;
  if (id == null || id === "") return null;
  return Number(id) || id;
};

/** Body items para POST /orden-pago/lotes → pagos_no_proveedor. */
export const mapFilasAPagosNoProveedorBody = (filas = []) => {
  const vistos = new Set();
  const items = [];
  (filas || []).forEach((doc) => {
    if (!esPagoNoProveedor(doc)) return;
    const id = idPagoNoProveedor(doc);
    if (id == null || vistos.has(String(id))) return;
    vistos.add(String(id));
    items.push({
      id_pago_no_proveedor: id,
    });
  });
  return items;
};

export const separarGruposProveedorYPagosVarios = (grupos = []) => {
  const proveedoresReales = [];
  const gruposPagosVarios = [];
  (grupos || []).forEach((g) => {
    if (g?.es_pagos_no_proveedor || String(g?.cuit) === CLAVE_PAGOS_NO_PROVEEDOR) {
      gruposPagosVarios.push(g);
    } else {
      proveedoresReales.push(g);
    }
  });
  return { proveedoresReales, gruposPagosVarios };
};

/** Agrupa filas seleccionadas por CUIT de proveedor (y pagos varios aparte). */
export const agruparFilasPorCuit = (filas = []) => {
  const map = new Map();
  filas.forEach((doc) => {
    if (esPagoNoProveedor(doc)) {
      if (!map.has(CLAVE_PAGOS_NO_PROVEEDOR)) {
        map.set(CLAVE_PAGOS_NO_PROVEEDOR, {
          cuit: CLAVE_PAGOS_NO_PROVEEDOR,
          razon_social:
            doc.razon_social || "Pagos varios / Impuestos y conceptos",
          es_pagos_no_proveedor: true,
          documentos: [],
        });
      }
      map.get(CLAVE_PAGOS_NO_PROVEEDOR).documentos.push(doc);
      return;
    }

    const cuit = String(doc?.nro_cuit || doc?.cuit || "").replace(/\D/g, "");
    if (!cuit) return;
    if (!map.has(cuit)) {
      map.set(cuit, {
        cuit,
        razon_social: doc.razon_social || "",
        cbu: doc.cbu || "",
        id_banco: doc.id_banco ?? null,
        banco_nombre: doc.banco_nombre || "",
        documentos: [],
      });
    }
    map.get(cuit).documentos.push(doc);
  });
  return Array.from(map.values());
};

/** Une previews de varios proveedores en uno con resumen_global. */
export const mergePreviewsProveedores = (items = []) => {
  const grupos = items.map(({ cuit, razon_social, preview }) => {
    const resumen = preview?.resumen_global ?? preview?.resumen ?? {};
    return {
      cuit,
      razon_social: razon_social || preview?.proveedor?.razon_social || "",
      comprobantes: preview?.comprobantes ?? [],
      retenciones: preview?.retenciones ?? {},
      resumen,
      proveedor: preview?.proveedor,
      advertencias: preview?.advertencias ?? [],
      detalle: preview?.detalle,
    };
  });

  const sum = (key) =>
    grupos.reduce((acc, g) => acc + (Number(g.resumen?.[key]) || 0), 0);
  const redondear = (x) => Math.round(x * 100) / 100;

  const requiereConfirmacion = items.some((item) =>
    Boolean(item?.preview?.requiere_confirmacion)
  );

  const resumen_global = {
    cantidad_comprobantes:
      sum("cantidad_comprobantes") ||
      grupos.reduce((a, g) => a + (g.comprobantes?.length || 0), 0),
    total_facturas: redondear(sum("total_facturas")),
    neto_imponible: redondear(sum("neto_imponible")),
    total_retenciones: redondear(sum("total_retenciones")),
    total_a_pagar: redondear(sum("total_a_pagar")),
    cantidad_facturas_con_retencion: sum("cantidad_facturas_con_retencion"),
    cantidad_facturas_sin_retencion: sum("cantidad_facturas_sin_retencion"),
    excedente_retencion: redondear(sum("excedente_retencion")),
    retencion_excede_pago: grupos.some((g) =>
      Boolean(g.resumen?.retencion_excede_pago)
    ),
  };

  if (grupos.length === 1) {
    const g = grupos[0];
    return {
      ok: true,
      multi_proveedor: false,
      grupos,
      comprobantes: g.comprobantes,
      retenciones: g.retenciones,
      proveedor: g.proveedor,
      resumen: g.resumen,
      resumen_global,
      detalle: g.detalle,
      advertencias: g.advertencias,
      requiere_confirmacion: requiereConfirmacion,
    };
  }

  return {
    ok: true,
    multi_proveedor: true,
    cantidad_proveedores: grupos.length,
    grupos,
    comprobantes: grupos.flatMap((g) => g.comprobantes ?? []),
    retenciones: { total: resumen_global.total_retenciones },
    resumen_global,
    resumen: resumen_global,
    advertencias: grupos.flatMap((g) => g.advertencias ?? []),
    requiere_confirmacion: requiereConfirmacion,
  };
};

const TOLERANCIA_TOTAL_PAGO = 0.009;

/** true si el back pide confirmación explícita antes de generar. */
export const previewRequiereConfirmacion = (preview) =>
  Boolean(
    preview?.requiere_confirmacion ||
      preview?.resumen?.retencion_excede_pago ||
      preview?.resumen_global?.retencion_excede_pago
  );

/** Excedente de retención informado por el back (0 si no aplica). */
export const excedenteRetencionPreview = (preview) => {
  const resumen = obtenerResumenPreview(preview);
  const excedente = Number(resumen?.excedente_retencion);
  if (Number.isFinite(excedente) && excedente > 0) return excedente;
  const total = Number(resumen?.total_a_pagar) || 0;
  return total < -TOLERANCIA_TOTAL_PAGO ? Math.round(-total * 100) / 100 : 0;
};

const r2Ret = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Suma importes activos de ganancias + IVA + IIBB. */
export const sumarImportesRetenciones = (retenciones) => {
  if (!retenciones) return 0;
  let total = 0;
  const gan = retenciones.ganancias;
  if (gan && (gan.aplica == null ? Number(gan.importe) > 0 : gan.aplica)) {
    total += Number(gan.importe) || 0;
  }
  const iva = retenciones.iva;
  if (iva && (iva.aplica == null ? Number(iva.importe) > 0 : iva.aplica)) {
    total += Number(iva.importe) || 0;
  }
  (retenciones.iibb ?? []).forEach((item) => {
    total += Number(item.importe) || 0;
  });
  return r2Ret(total);
};

/**
 * Lista plana de retenciones editables de un grupo/preview.
 * clave: "ganancias" | "iva" | "iibb:<id|provincia|idx>"
 */
export const listarRetencionesEditables = (retenciones) => {
  const items = [];
  if (!retenciones) return items;

  const gan = retenciones.ganancias;
  if (gan) {
    const importe = Number(gan.importe) || 0;
    const aplica = gan.aplica != null ? Boolean(gan.aplica) : importe > 0;
    if (importe > 0) {
      items.push({
        clave: "ganancias",
        tipo: "ganancias",
        label: "Retención Ganancias",
        importe,
        aplica,
        base: gan.base ?? gan.base_imponible,
        alicuota: gan.alicuota,
      });
    }
  }

  const iva = retenciones.iva;
  if (iva) {
    const importe = Number(iva.importe) || 0;
    const aplica = iva.aplica != null ? Boolean(iva.aplica) : importe > 0;
    if (importe > 0) {
      items.push({
        clave: "iva",
        tipo: "iva",
        label: "Retención IVA",
        importe,
        aplica,
        base: iva.base ?? iva.base_imponible,
        alicuota: iva.alicuota,
      });
    }
  }

  (retenciones.iibb ?? []).forEach((item, idx) => {
    const importe = Number(item.importe) || 0;
    if (importe <= 0) return;
    const idKey = item.id ?? item.id_provincia ?? idx;
    items.push({
      clave: `iibb:${idKey}`,
      tipo: "iibb",
      label: item.certificado || `IIBB ${item.provincia || ""}`.trim() || "IIBB",
      importe,
      aplica: true,
      base: item.base ?? item.base_imponible,
      alicuota: item.alicuota,
      id_provincia: item.id_provincia,
      id: item.id,
      idx,
    });
  });

  return items;
};

const patchRetencionEnObjeto = (retenciones, clave, { importe, eliminar }) => {
  const next = {
    ...(retenciones || {}),
    iibb: [...(retenciones?.iibb ?? [])],
  };

  if (clave === "ganancias") {
    if (eliminar || Number(importe) <= 0) {
      if (next.ganancias) {
        next.ganancias = {
          ...next.ganancias,
          aplica: false,
          importe: 0,
        };
      }
    } else if (next.ganancias) {
      next.ganancias = {
        ...next.ganancias,
        aplica: true,
        importe: r2Ret(importe),
      };
    } else {
      next.ganancias = {
        aplica: true,
        importe: r2Ret(importe),
      };
    }
  } else if (clave === "iva") {
    if (eliminar || Number(importe) <= 0) {
      if (next.iva) {
        next.iva = { ...next.iva, aplica: false, importe: 0 };
      }
    } else if (next.iva) {
      next.iva = { ...next.iva, aplica: true, importe: r2Ret(importe) };
    } else {
      next.iva = { aplica: true, importe: r2Ret(importe) };
    }
  } else if (String(clave).startsWith("iibb:")) {
    const idKey = String(clave).slice(5);
    next.iibb = next.iibb
      .map((item, idx) => {
        const key = String(item.id ?? item.id_provincia ?? idx);
        if (key !== idKey) return item;
        if (eliminar || Number(importe) <= 0) {
          return { ...item, importe: 0 };
        }
        return { ...item, importe: r2Ret(importe) };
      })
      .filter((item) => Number(item.importe) > 0);
  }

  const total = sumarImportesRetenciones(next);
  next.total = total;
  return next;
};

const recalcularResumenTrasRetenciones = (resumenPrev, totalRetNuevo) => {
  const prev = resumenPrev || {};
  const totalRetViejo = r2Ret(prev.total_retenciones);
  const totalRet = r2Ret(totalRetNuevo);
  const delta = r2Ret(totalRetViejo - totalRet);
  const totalAPagar = r2Ret((Number(prev.total_a_pagar) || 0) + delta);
  const excedente =
    totalAPagar < -TOLERANCIA_TOTAL_PAGO ? r2Ret(-totalAPagar) : 0;

  return {
    ...prev,
    total_retenciones: totalRet,
    total_a_pagar: totalAPagar,
    excedente_retencion: excedente,
    retencion_excede_pago: excedente > 0 || totalAPagar < -TOLERANCIA_TOTAL_PAGO,
  };
};

const actualizarTotalesDetalleRetenciones = (detalle, retenciones) => {
  if (!detalle?.resumen) return detalle;
  const gan = retenciones?.ganancias;
  const iva = retenciones?.iva;
  const iibb = retenciones?.iibb ?? [];
  const totalGan =
    gan && (gan.aplica == null ? Number(gan.importe) > 0 : gan.aplica)
      ? Number(gan.importe) || 0
      : 0;
  const totalIva =
    iva && (iva.aplica == null ? Number(iva.importe) > 0 : iva.aplica)
      ? Number(iva.importe) || 0
      : 0;
  const totalIibb = iibb.reduce((a, i) => a + (Number(i.importe) || 0), 0);
  const total = r2Ret(totalGan + totalIva + totalIibb);

  return {
    ...detalle,
    resumen: {
      ...detalle.resumen,
      total_retenciones_ganancias: r2Ret(totalGan),
      total_retenciones_iva: r2Ret(totalIva),
      total_retenciones_iibb: r2Ret(totalIibb),
      total_retenciones: total,
    },
  };
};

/**
 * Edita/elimina una retención de un solo proveedor (CUIT) sin tocar el resto.
 * Recalcula totales del grupo y del resumen global.
 */
export const editarRetencionPreviewProveedor = (
  preview,
  cuit,
  clave,
  cambios
) => {
  if (!preview) return preview;
  const cuitNorm = String(cuit || "").replace(/\D/g, "");

  const gruposBase =
    Array.isArray(preview.grupos) && preview.grupos.length > 0
      ? preview.grupos
      : [
          {
            cuit: preview?.proveedor?.nro_cuit || cuitNorm,
            razon_social: preview?.proveedor?.razon_social || "",
            comprobantes: preview?.comprobantes ?? [],
            retenciones: preview?.retenciones ?? {},
            resumen: preview?.resumen ?? preview?.resumen_global ?? {},
            proveedor: preview?.proveedor,
            advertencias: preview?.advertencias ?? [],
            detalle: preview?.detalle,
          },
        ];

  const grupos = gruposBase.map((grupo) => {
    const gCuit = String(grupo.cuit || "").replace(/\D/g, "");
    if (gCuit !== cuitNorm) return grupo;

    const retenciones = patchRetencionEnObjeto(
      grupo.retenciones,
      clave,
      cambios
    );
    const totalRet = sumarImportesRetenciones(retenciones);
    const resumen = recalcularResumenTrasRetenciones(grupo.resumen, totalRet);
    const detalle = actualizarTotalesDetalleRetenciones(
      grupo.detalle,
      retenciones
    );

    return {
      ...grupo,
      retenciones,
      resumen,
      detalle,
      retenciones_ajustadas: true,
    };
  });

  const sum = (key) =>
    grupos.reduce((acc, g) => acc + (Number(g.resumen?.[key]) || 0), 0);

  const resumen_global = {
    ...(preview.resumen_global || preview.resumen || {}),
    cantidad_comprobantes:
      sum("cantidad_comprobantes") ||
      grupos.reduce((a, g) => a + (g.comprobantes?.length || 0), 0),
    total_facturas: r2Ret(sum("total_facturas")),
    neto_imponible: r2Ret(sum("neto_imponible")),
    total_retenciones: r2Ret(sum("total_retenciones")),
    total_a_pagar: r2Ret(sum("total_a_pagar")),
    excedente_retencion: r2Ret(sum("excedente_retencion")),
    retencion_excede_pago: grupos.some((g) =>
      Boolean(g.resumen?.retencion_excede_pago)
    ),
  };

  if (grupos.length === 1 && !preview.multi_proveedor) {
    const g = grupos[0];
    return {
      ...preview,
      grupos,
      retenciones: g.retenciones,
      resumen: g.resumen,
      resumen_global,
      detalle: g.detalle,
      retenciones_ajustadas: true,
    };
  }

  return {
    ...preview,
    multi_proveedor: grupos.length > 1,
    cantidad_proveedores: grupos.length,
    grupos,
    comprobantes: grupos.flatMap((g) => g.comprobantes ?? []),
    retenciones: { total: resumen_global.total_retenciones },
    resumen_global,
    resumen: resumen_global,
    retenciones_ajustadas: true,
  };
};

/** Body de retenciones a enviar al generar (si fueron ajustadas). */
export const retencionesBodyDesdeGrupo = (previewGrupo, previewFallback) => {
  const ret =
    previewGrupo?.retenciones ??
    (previewFallback && !previewFallback.multi_proveedor
      ? previewFallback.retenciones
      : null);
  if (!ret) return null;

  const ajustadas = Boolean(
    previewGrupo?.retenciones_ajustadas || previewFallback?.retenciones_ajustadas
  );
  if (!ajustadas) return null;

  const body = { total: sumarImportesRetenciones(ret) };
  if (ret.ganancias) {
    body.ganancias = {
      ...ret.ganancias,
      importe: Number(ret.ganancias.importe) || 0,
      aplica:
        ret.ganancias.aplica != null
          ? Boolean(ret.ganancias.aplica)
          : Number(ret.ganancias.importe) > 0,
    };
  }
  if (ret.iva) {
    body.iva = {
      ...ret.iva,
      importe: Number(ret.iva.importe) || 0,
      aplica:
        ret.iva.aplica != null
          ? Boolean(ret.iva.aplica)
          : Number(ret.iva.importe) > 0,
    };
  }
  body.iibb = (ret.iibb ?? [])
    .filter((i) => Number(i.importe) > 0)
    .map((i) => ({
      ...i,
      importe: Number(i.importe) || 0,
    }));

  return body;
};

/**
 * Cuando el total queda en cero o negativo no hay nada para transferir:
 * el back acepta medios_pago vacío y guarda la OP como tipo_medio "ajuste".
 */
export const requiereMediosPago = (totalAPagar) =>
  (Number(totalAPagar) || 0) > TOLERANCIA_TOTAL_PAGO;

/** Total contra el que se valida la distribución de medios. */
export const totalParaMedios = (totalAPagar) =>
  Math.max(0, Number(totalAPagar) || 0);

/** true si el comprobante retiene en este pago (false = queda parcial). */
export const comprobanteRetiene = (comp) => {
  if (comp?.retiene != null) return Boolean(comp.retiene);
  if (comp?.cancela_factura != null) return Boolean(comp.cancela_factura);
  return Number(comp?.neto_imponible_aportado ?? 0) > TOLERANCIA_TOTAL_PAGO;
};

/** Mapa idcomprobante → flags de retención, para pintar la grilla de pendientes. */
export const retencionPorComprobante = (preview) => {
  const mapa = new Map();
  for (const comp of obtenerComprobantesPreview(preview)) {
    if (comp?.idcomprobante == null) continue;
    mapa.set(String(comp.idcomprobante), {
      retiene: comprobanteRetiene(comp),
      cancela_factura: Boolean(comp.cancela_factura ?? comp.retiene),
      neto_imponible_aportado: Number(comp.neto_imponible_aportado ?? 0),
    });
  }
  return mapa;
};

/** Ajusta importes de cuentas origen para que sumen totalDestino. */
export const ajustarCuentasATotal = (cuentas = [], totalDestino) => {
  const list = (cuentas || []).filter((c) => c?.id_bancos_cuentas);
  const dest = Math.round((Number(totalDestino) || 0) * 100) / 100;
  if (!list.length || dest <= 0) return [];
  if (list.length === 1) {
    return [{ ...list[0], importe: dest }];
  }
  const suma = list.reduce((a, c) => a + (Number(c.importe) || 0), 0);
  if (suma <= 0) {
    return [{ ...list[0], importe: dest }];
  }
  let acum = 0;
  return list.map((c, i) => {
    if (i === list.length - 1) {
      return { ...c, importe: Math.round((dest - acum) * 100) / 100 };
    }
    const importe =
      Math.round(((Number(c.importe) || 0) / suma) * dest * 100) / 100;
    acum += importe;
    return { ...c, importe };
  });
};

/** Comprobantes del preview (forma plana del backend). */
export const obtenerComprobantesPreview = (preview) =>
  preview?.comprobantes ??
  obtenerGruposPreview(preview).flatMap((g) => g.comprobantes ?? []);

/** Empresas únicas a partir de documentos pendientes. */
export const empresasDesdeDocumentos = (documentos = []) => {
  const map = new Map();
  documentos.forEach((doc) => {
    const id = doc.id_empresa;
    if (id == null) return;
    if (!map.has(id)) {
      map.set(id, {
        id_empresa: id,
        nombre: doc.nombre_empresa || `Empresa ${id}`,
      });
    }
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.nombre).localeCompare(String(b.nombre), "es")
  );
};

const COLORES_EMPRESA = [
  "rgba(36, 113, 163, 0.06)",
  "rgba(46, 125, 50, 0.06)",
  "rgba(237, 108, 2, 0.06)",
  "rgba(123, 31, 162, 0.06)",
  "rgba(0, 131, 143, 0.06)",
];

export const colorFondoEmpresa = (idEmpresa) => {
  if (idEmpresa == null) return "#FFFFFF";
  const idx = Math.abs(Number(idEmpresa)) % COLORES_EMPRESA.length;
  return COLORES_EMPRESA[idx];
};

export const normalizarMensajeErrorPreview = (mensaje) => {
  const msg = String(mensaje || "");
  if (/id_empresa/i.test(msg) && /asignado/i.test(msg)) {
    return "Uno o más comprobantes no tienen empresa asignada. Revise el alta de facturas antes de generar la orden de pago.";
  }
  if (/cheque/i.test(msg) && /(disponible|endosad|usado|orden)/i.test(msg)) {
    return `${msg.replace(/\.$/, "")}. Volvé al paso de medio de pago para quitar ese cheque y elegir otro.`;
  }
  return msg;
};

const TOLERANCIA_IMPORTE = 0.01;

/** Clave estable de un documento de pendientes (factura, adelanto o pago varios). */
export const idDocumentoPendiente = (doc) => {
  if (esPagoNoProveedor(doc)) {
    const id = idPagoNoProveedor(doc);
    if (id != null) return `pnp-${id}`;
  }
  if (doc?.idcomprobante != null && doc.idcomprobante !== "") {
    return String(doc.idcomprobante);
  }
  if (doc?.id_anticipo != null && doc.id_anticipo !== "") {
    return `adelanto-${doc.id_anticipo}`;
  }
  if (doc?.id_orden_pago != null && doc.id_orden_pago !== "") {
    return `op-adelanto-${doc.id_orden_pago}`;
  }
  return null;
};

export const esDocumentoAdelanto = (doc) =>
  Boolean(doc?.es_adelanto) ||
  String(doc?.tipo_comprobante || "").toLowerCase() === "adelanto";

const normalizarTipoComprobante = (tipo) =>
  String(tipo || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

/** Fallback: 2 letras del primer término + inicial del siguiente (o letra A/B/C…). */
const fallbackAbrevTipoComprobante = (tipo) => {
  const texto = String(tipo || "").trim();
  if (!texto) return "";
  const palabras = texto.split(/\s+/).filter(Boolean);
  const primero = palabras[0] || "";
  const dos = primero.slice(0, 2);
  if (!dos) return "";
  const ultima = palabras[palabras.length - 1] || "";
  const letraTipo = /^[A-Z]$/i.test(ultima) && palabras.length > 1 ? ultima : "";
  const inicial =
    letraTipo ||
    (palabras.length > 1 ? (palabras[1][0] || "") : "");
  return `${dos.charAt(0).toUpperCase()}${dos.slice(1).toLowerCase()}${inicial.toUpperCase()}`;
};

const abrevDesdeNombreTipo = (tipo) => {
  const t = normalizarTipoComprobante(tipo);
  if (!t) return "";
  if (t === "ADELANTO" || t.startsWith("ADELANTO ")) return "Adel";
  if (/^RECIBO\b/.test(t) || t === "RC") return "RC";
  const fc = t.match(/\bFACTURA\s*([ABCEM])\b/) || t.match(/^FC\s*([ABCEM])$/);
  if (fc) return `Fc${fc[1]}`;
  const nc =
    t.match(/\bNOTA\s+DE\s+CREDITO\s*([ABCEM])\b/) ||
    t.match(/\bN\.?\s*C\.?\s*([ABCEM])\b/) ||
    t.match(/^NC\s*([ABCEM])$/);
  if (nc) return `NC${nc[1]}`;
  const nd =
    t.match(/\bNOTA\s+DE\s+DEBITO\s*([ABC])\b/) ||
    t.match(/\bN\.?\s*D\.?\s*([ABC])\b/) ||
    t.match(/^ND\s*([ABC])$/);
  if (nd) return `ND${nd[1]}`;
  return fallbackAbrevTipoComprobante(tipo);
};

/**
 * Abreviatura para grillas: usa `tipo_comprobante_abrev` del API si viene;
 * si no, mapa habitual / concepto PNP / fallback. No rompe si viene vacío.
 */
export const abreviaturaTipoComprobante = (doc) => {
  if (!doc) return "";
  if (esDocumentoAdelanto(doc)) {
    const api = String(doc.tipo_comprobante_abrev || "").trim();
    return api || "Adel";
  }
  if (esPagoNoProveedor(doc)) {
    const api = String(doc.tipo_comprobante_abrev || "").trim();
    if (api) return api;
    return abrevConcepto(doc.concepto || doc.tipo_comprobante);
  }
  const api = String(doc.tipo_comprobante_abrev || "").trim();
  if (api) return api;
  return abrevDesdeNombreTipo(doc.tipo_comprobante);
};

export const nroComprobanteDisplay = (doc) => {
  if (!doc) return "";
  if (esPagoNoProveedor(doc)) {
    return String(doc.motivo || doc.nro_comprobante || "").trim();
  }
  return String(doc.nro_comprobante || "").trim();
};

/** Texto de columna: `FcA 0001-00001234`. */
export const etiquetaComprobanteCorta = (doc) => {
  const etiqueta = [abreviaturaTipoComprobante(doc), nroComprobanteDisplay(doc)]
    .filter(Boolean)
    .join(" ");
  return etiqueta || "—";
};

/** Tooltip / detalle: nombre largo (`tipo_comprobante`). */
export const tooltipTipoComprobante = (doc) => {
  if (!doc) return "";
  if (esPagoNoProveedor(doc)) {
    const label = labelConcepto(doc.concepto);
    if (label && label !== "—") return label;
    return String(doc.tipo_comprobante || "Pago varios").trim();
  }
  return String(doc.tipo_comprobante || "").trim();
};

/** Detecta tipo NC sin falsos positivos (p. ej. "BANCO" contiene "NC"). */
const esTipoNotaCredito = (tipoRaw) => {
  const tipo = String(tipoRaw || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
  if (!tipo) return false;
  if (tipo.includes("NOTA DE CREDITO")) return true;
  // "NC", "NC A", "NC-B", "FAC NC", etc. — no "BANCO"
  if (/(?:^|[^A-Z])NC(?:[^A-Z]|$)/.test(tipo)) return true;
  // Variantes cortas: NCA, NCB, NCC…
  return /^NC[A-Z]$/.test(tipo);
};

/** Nota de crédito u otro comprobante con saldo a favor (resta del pago). */
export const esComprobanteCredito = (saldoMax, doc) => {
  if (esDocumentoAdelanto(doc)) return true;
  const saldo = Number(saldoMax);
  if (Number.isFinite(saldo) && saldo < -TOLERANCIA_IMPORTE) return true;
  return esTipoNotaCredito(doc?.tipo_comprobante);
};

export const MENSAJE_ERROR_IMPORTES =
  "Revise los importes: en comprobantes a pagar deben ser mayores a 0 y no superar el saldo; en notas de crédito deben ser negativos y no superar el saldo a favor.";

/** Monto completo de un pago varios (no admite pago parcial). */
export const importeTotalPagoNoProveedor = (doc, saldoMax) => {
  const total = Number(
    saldoMax ?? doc?.saldo_pendiente ?? doc?.monto ?? doc?.total
  );
  return Number.isFinite(total) ? Math.round(total * 100) / 100 : total;
};

/** Límites min/max para el input numérico según signo del saldo. */
export const limitesImporteInput = (saldoMax, doc) => {
  if (esPagoNoProveedor(doc)) {
    const total = importeTotalPagoNoProveedor(doc, saldoMax);
    return { min: total, max: total, step: 0.01, readOnly: true };
  }
  const saldo = Number(saldoMax);
  if (esComprobanteCredito(saldoMax, doc)) {
    const min = Number.isFinite(saldo) && saldo < 0 ? saldo : undefined;
    return { min, max: -TOLERANCIA_IMPORTE, step: 0.01 };
  }
  return {
    min: TOLERANCIA_IMPORTE,
    max: Number.isFinite(saldo) && saldo > 0 ? saldo : undefined,
    step: 0.01,
  };
};

export const clampImporte = (valor, saldoMax, doc) => {
  if (esPagoNoProveedor(doc)) {
    return importeTotalPagoNoProveedor(doc, saldoMax);
  }
  const saldo = Number(saldoMax);
  const n = parseFloat(String(valor ?? "").replace(",", "."));
  const redondear = (x) => Math.round(x * 100) / 100;

  if (esComprobanteCredito(saldoMax, doc)) {
    if (!Number.isFinite(saldo) || saldo >= 0) return saldo;
    if (!Number.isFinite(n) || n >= 0) return redondear(saldo);
    return redondear(Math.max(saldo, Math.min(n, -TOLERANCIA_IMPORTE)));
  }

  if (!Number.isFinite(saldo) || saldo <= 0) return saldo;
  if (!Number.isFinite(n) || n <= 0) return redondear(saldo);
  return redondear(Math.min(n, saldo));
};

export const importeEsValido = (valor, saldoMax, doc) => {
  if (esPagoNoProveedor(doc)) {
    const total = importeTotalPagoNoProveedor(doc, saldoMax);
    const importe = Number(valor);
    if (!Number.isFinite(importe) || !Number.isFinite(total)) return false;
    return Math.abs(importe - total) <= TOLERANCIA_IMPORTE;
  }
  const importe = Number(valor);
  const saldo = Number(saldoMax);
  if (!Number.isFinite(importe) || !Number.isFinite(saldo)) return false;

  if (esComprobanteCredito(saldoMax, doc)) {
    return (
      importe < 0 &&
      importe >= saldo - TOLERANCIA_IMPORTE
    );
  }

  return importe > 0 && importe <= saldo + TOLERANCIA_IMPORTE;
};

/**
 * Tope de importe al editar una OP pendiente.
 * Preferencia: importe_maximo / saldo_disponible del GET detalle.
 * Fallback: reconstruye cupo (saldo libre + lo ya imputado por esta OP).
 */
export const saldoMaximoParaEdicionOp = (doc) => {
  const r2 = (n) => Math.round(Number(n) * 100) / 100;
  const n = (v) => (v != null && v !== "" ? Number(v) : NaN);

  const topeApi = n(doc?.importe_maximo ?? doc?.saldo_disponible);
  if (Number.isFinite(topeApi) && Math.abs(topeApi) > TOLERANCIA_IMPORTE) {
    if (
      esDocumentoAdelanto(doc) ||
      esComprobanteCredito(doc?.saldo_pendiente, doc) ||
      topeApi < 0
    ) {
      return r2(-Math.abs(topeApi));
    }
    return r2(Math.abs(topeApi));
  }

  const importe = n(doc?.importe_a_pagar ?? doc?.importe);
  const total = n(doc?.total ?? doc?.total_comprobante);
  const saldoAntes = n(doc?.saldo_antes);
  if (Number.isFinite(saldoAntes)) {
    if (Number.isFinite(total) && Math.abs(total) > TOLERANCIA_IMPORTE) {
      if (saldoAntes >= 0 && total >= 0) return r2(Math.min(saldoAntes, total));
      if (saldoAntes <= 0 && total <= 0) return r2(Math.max(saldoAntes, total));
    }
    return r2(saldoAntes);
  }

  const saldoDespues = n(doc?.saldo_despues);
  const saldoRestante = n(doc?.saldo_restante);
  const saldoPend = n(doc?.saldo_pendiente);
  const saldoLive = Number.isFinite(saldoDespues)
    ? saldoDespues
    : Number.isFinite(saldoRestante)
      ? saldoRestante
      : saldoPend;

  let max;
  if (
    Number.isFinite(saldoLive) &&
    Number.isFinite(importe) &&
    Math.abs(importe) > TOLERANCIA_IMPORTE
  ) {
    const reconstruido = r2(saldoLive + importe);
    // Si saldo_pendiente ya es pre-OP (p.ej. = total), no sumar de nuevo.
    if (
      Number.isFinite(total) &&
      Math.abs(saldoLive - total) <= TOLERANCIA_IMPORTE
    ) {
      max = r2(saldoLive);
    } else {
      max = reconstruido;
    }
  } else if (Number.isFinite(saldoLive)) {
    max = r2(saldoLive);
  } else if (Number.isFinite(total)) {
    max = r2(total);
  } else if (Number.isFinite(importe)) {
    max = r2(importe);
  } else {
    return 0;
  }

  if (Number.isFinite(total) && Math.abs(total) > TOLERANCIA_IMPORTE) {
    if (max >= 0 && total >= 0) return r2(Math.min(max, total));
    if (max <= 0 && total <= 0) return r2(Math.max(max, total));
  }

  return max;
};

/** Ítem de comprobante/adelanto para preview-retenciones y generar. */
export const mapFilaAComprobanteBody = (row, importesAPagar = {}) => {
  if (esPagoNoProveedor(row)) {
    throw new Error(
      "Los pagos varios no se envían como comprobantes; usan pagos_no_proveedor"
    );
  }

  const id = idDocumentoPendiente(row);
  const importe_a_pagar = clampImporte(
    (id != null ? importesAPagar[id] : undefined) ??
      row.importe_a_pagar ??
      row.saldo_pendiente,
    row.saldo_pendiente,
    row
  );

  if (esDocumentoAdelanto(row)) {
    // El back usa todo el saldo si importe_a_pagar viene null/undefined.
    if (!Number.isFinite(Number(importe_a_pagar))) {
      throw new Error(
        "El adelanto debe incluir importe_a_pagar (crédito a aplicar)"
      );
    }
    return {
      ...(row.id_anticipo != null ? { id_anticipo: Number(row.id_anticipo) } : {}),
      ...(row.id_orden_pago != null
        ? { id_orden_pago: Number(row.id_orden_pago) }
        : {}),
      es_adelanto: true,
      importe_a_pagar: Number(importe_a_pagar),
    };
  }

  return {
    idcomprobante: row.idcomprobante,
    importe_a_pagar,
  };
};

/** Importe efectivo de una fila (estado del wizard o `importe_a_pagar` del preview). */
export const importeSeleccionado = (doc, importesAPagar = {}) => {
  if (esPagoNoProveedor(doc)) {
    return importeTotalPagoNoProveedor(doc);
  }
  const id = idDocumentoPendiente(doc);
  const fromState = id != null ? importesAPagar[id] : undefined;
  if (fromState != null && fromState !== "") {
    return Number(fromState);
  }
  if (doc.importe_a_pagar != null && doc.importe_a_pagar !== "") {
    return Number(doc.importe_a_pagar);
  }
  return Number(doc.saldo_pendiente);
};

/** Totales de débitos y notas de crédito para el resumen del pago. */
export const resumenImportesSeleccion = (filas = [], importesAPagar = {}) => {
  let totalDebitos = 0;
  let totalNotasCredito = 0;

  for (const doc of filas) {
    const importe = importeSeleccionado(doc, importesAPagar);
    if (!Number.isFinite(importe)) continue;
    if (esComprobanteCredito(doc.saldo_pendiente, doc)) {
      totalNotasCredito += Math.abs(importe);
    } else if (importe > 0) {
      totalDebitos += importe;
    }
  }

  const redondear = (x) => Math.round(x * 100) / 100;
  totalDebitos = redondear(totalDebitos);
  totalNotasCredito = redondear(totalNotasCredito);

  return {
    totalDebitos,
    totalNotasCredito,
    subtotal: redondear(totalDebitos - totalNotasCredito),
    tieneNotasCredito: totalNotasCredito > TOLERANCIA_IMPORTE,
  };
};

/**
 * Ajusta importes de adelantos para no aplicar más crédito del necesario.
 * Ej.: factura 10.000 + adelanto 50.000 → adelanto en -10.000 (el resto queda a favor).
 * Si no hay facturas, no modifica adelantos.
 * Devuelve { importes, deseleccionar } (adelantos sin cupo a aplicar).
 */
export const ajustarImportesAdelantos = (
  filasSeleccionadas = [],
  importesAPagar = {}
) => {
  const r2 = (x) => Math.round(Number(x) * 100) / 100;
  const next = { ...importesAPagar };
  const deseleccionar = [];
  const grupos = agruparFilasPorCuit(filasSeleccionadas);

  for (const grupo of grupos) {
    const docs = grupo.documentos || [];
    const adelantos = docs.filter(esDocumentoAdelanto);
    if (!adelantos.length) continue;

    let totalDebitos = 0;
    let totalOtrosCreditos = 0;

    for (const doc of docs) {
      if (esDocumentoAdelanto(doc)) continue;
      const id = idDocumentoPendiente(doc);
      const raw = id != null ? next[id] : undefined;
      const importe = Number(
        raw != null && raw !== "" ? raw : doc.saldo_pendiente
      );
      if (!Number.isFinite(importe)) continue;
      if (esComprobanteCredito(doc.saldo_pendiente, doc)) {
        totalOtrosCreditos += Math.abs(importe);
      } else if (importe > 0) {
        totalDebitos += importe;
      }
    }

    totalDebitos = r2(totalDebitos);
    totalOtrosCreditos = r2(totalOtrosCreditos);

    // Sin facturas: no forzar parcial ni deseleccionar adelantos.
    if (totalDebitos <= TOLERANCIA_IMPORTE) continue;

    let restante = r2(Math.max(0, totalDebitos - totalOtrosCreditos));

    for (const ad of adelantos) {
      const id = idDocumentoPendiente(ad);
      if (!id) continue;

      const rawActual = next[id];
      // No pisar mientras el usuario está tipeando un valor incompleto.
      if (
        rawActual != null &&
        rawActual !== "" &&
        !Number.isFinite(Number(String(rawActual).replace(",", ".")))
      ) {
        continue;
      }

      const saldoAbs = Math.abs(Number(ad.saldo_pendiente) || 0);
      // Aplicar solo lo necesario para cubrir facturas; el resto del adelanto queda a favor.
      const aplicar = r2(Math.min(saldoAbs, restante));

      if (aplicar < TOLERANCIA_IMPORTE) {
        deseleccionar.push(id);
        delete next[id];
        continue;
      }

      next[id] = -aplicar;
      restante = r2(Math.max(0, restante - aplicar));
    }
  }

  return { importes: next, deseleccionar };
};

const redondearImporte = (x) => Math.round(Number(x || 0) * 100) / 100;

/** Preview local de adelantos: sin retenciones ni llamada al API. */
export const buildPreviewAdelantosLocal = (docs = [], importesAPagar = {}) => {
  const adelantos = (docs || []).filter(esDocumentoAdelanto);
  let totalAdelantos = 0;

  const comprobantes = adelantos.map((row) => {
    const body = mapFilaAComprobanteBody(row, importesAPagar);
    totalAdelantos += Math.abs(Number(body.importe_a_pagar) || 0);
    return {
      ...body,
      tipo_comprobante: row.tipo_comprobante || "Adelanto",
      tipo_comprobante_abrev: row.tipo_comprobante_abrev || "Adel",
      nro_comprobante: row.nro_comprobante,
      saldo_pendiente: row.saldo_pendiente,
      descripcion_breve: row.descripcion_breve,
      es_adelanto: true,
      es_credito: true,
    };
  });

  totalAdelantos = redondearImporte(totalAdelantos);

  return {
    ok: true,
    comprobantes,
    retenciones: { total: 0 },
    resumen: {
      cantidad_comprobantes: comprobantes.length,
      total_facturas: 0,
      neto_imponible: 0,
      total_retenciones: 0,
      total_adelantos: totalAdelantos,
      total_a_pagar: redondearImporte(-totalAdelantos),
    },
  };
};

/** Suma adelantos (crédito, sin retenciones) a un preview de facturas. */
export const mergeAdelantosEnPreview = (
  previewApi,
  docsAdelanto = [],
  importesAPagar = {}
) => {
  const adelantos = (docsAdelanto || []).filter(esDocumentoAdelanto);
  if (!adelantos.length) return previewApi;

  const local = buildPreviewAdelantosLocal(adelantos, importesAPagar);
  const totalAdelantos = Number(local.resumen.total_adelantos) || 0;
  const base = previewApi || {
    ok: true,
    comprobantes: [],
    retenciones: { total: 0 },
    resumen: {},
  };
  const resumenBase = base.resumen_global ?? base.resumen ?? {};
  const resumen = {
    ...resumenBase,
    cantidad_comprobantes:
      (Number(resumenBase.cantidad_comprobantes) ||
        (base.comprobantes?.length ?? 0)) + local.comprobantes.length,
    total_adelantos: redondearImporte(
      (Number(resumenBase.total_adelantos) || 0) + totalAdelantos
    ),
    total_a_pagar: redondearImporte(
      (Number(resumenBase.total_a_pagar) || 0) - totalAdelantos
    ),
    total_retenciones: Number(resumenBase.total_retenciones) || 0,
  };

  return {
    ...base,
    ok: true,
    comprobantes: [...(base.comprobantes ?? []), ...local.comprobantes],
    retenciones: base.retenciones ?? { total: 0 },
    resumen,
    ...(base.resumen_global
      ? {
          resumen_global: {
            ...base.resumen_global,
            ...resumen,
          },
        }
      : {}),
  };
};

export const sumarImportesCuentas = (cuentasOrigen = []) =>
  cuentasOrigen.reduce((acc, c) => acc + (Number(c.importe) || 0), 0);

export const restanteDistribucion = (cuentasOrigen, totalAPagar) => {
  const total = Number(totalAPagar) || 0;
  return Math.round((total - sumarImportesCuentas(cuentasOrigen)) * 100) / 100;
};

/** Clave de agrupación por banco (misma lógica que el backend). */
export const claveBancoCuenta = (fila) => {
  const cuenta = fila?.cuenta ?? {};
  const idBanco = cuenta.id_banco ?? fila?.codigoBanco;
  if (idBanco != null && idBanco !== "") return `banco:${idBanco}`;
  const cbu = cuenta.cbu;
  if (cbu) return `cbu:${cbu}`;
  return fila?.id_bancos_cuentas
    ? `cuenta:${fila.id_bancos_cuentas}`
    : null;
};

/** Agrupa cuentas origen por banco para estimar cuántas OP se generarán. */
export const agruparCuentasPorBanco = (cuentasOrigenDetalle = []) => {
  const grupos = new Map();

  cuentasOrigenDetalle
    .filter((f) => f?.id_bancos_cuentas)
    .forEach((fila) => {
      const key = claveBancoCuenta(fila);
      if (!key) return;

      if (!grupos.has(key)) {
        const cuenta = fila.cuenta ?? {};
        grupos.set(key, {
          clave: key,
          id_banco: cuenta.id_banco ?? fila.codigoBanco ?? null,
          banco_nombre:
            cuenta.razon_social ||
            cuenta.banco_nombre ||
            (cuenta.id_banco != null ? `Banco ${cuenta.id_banco}` : "Banco"),
          importe: 0,
          cuentas: [],
        });
      }

      const grupo = grupos.get(key);
      grupo.importe += Number(fila.importe) || 0;
      grupo.cuentas.push(fila);
    });

  return Array.from(grupos.values()).map((g) => ({
    ...g,
    importe: Math.round(g.importe * 100) / 100,
  }));
};

/** Multi-medio: siempre una OP unificada (no se parte por banco origen). */
export const cantidadOpsEstimada = () => 1;

export const enriquecerCuentasOrigen = (cuentasOrigen = [], cuentasBancarias = []) =>
  cuentasOrigen.map((fila) => {
    const cuenta = cuentasBancarias.find(
      (c) => String(c.id_bancos_cuentas) === String(fila.id_bancos_cuentas)
    );
    return { ...fila, cuenta };
  });

const TOLERANCIA_MEDIOS = 0.02;

/** Tope de autonomía para Ajustes Varios (diferencias chicas). */
export const AJUSTE_AUTONOMIA_VARIOS = 100;

export const CONCEPTOS_AJUSTE = {
  varios: "varios",
  internos: "internos",
};

export const ETIQUETAS_CONCEPTO_AJUSTE = {
  varios: "Ajustes Varios",
  internos: "Ajustes internos",
};

export const etiquetaConceptoAjuste = (concepto) =>
  ETIQUETAS_CONCEPTO_AJUSTE[concepto] || "Ajuste";

const redondear2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const parseImporteMedio = (valor) => {
  if (valor === "" || valor == null) return 0;
  const n = Number(String(valor).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

/** Estado inicial del paso medio de pago (multi-medio). */
export const medioPagoInicial = (fechaPago = null) => ({
  fechaPago,
  observaciones: "",
  efectivo: { activo: false, importe: "" },
  transferencia: { activo: true, cuentasOrigen: [] },
  compensacion: {
    activo: false,
    importe: "",
    saldoDisponible: null,
    idCliente: null,
    razonSocial: null,
    loading: false,
    error: null,
  },
  debitoAutomatico: {
    activo: false,
    importe: "",
    ultimos4: "",
    referencia: "",
  },
  tarjetaCredito: {
    activo: false,
    importe: "",
    ultimos4: "",
    banco: "",
    referencia: "",
  },
  ajuste: {
    activo: false,
    importe: "",
    concepto: CONCEPTOS_AJUSTE.varios,
    descripcion: "",
  },
  cheque: {
    activo: false,
    recibidos: [],
    emitidos: [],
  },
});

/** Medio de pago elegido por proveedor en la pantalla de comprobantes. */
export const MEDIO_PAGO_PROVEEDOR = {
  TRANSFERENCIA: "transferencia",
  CHEQUE: "cheque",
  ECHEQUE: "echeque",
};

export const OPCIONES_MEDIO_PAGO_PROVEEDOR = [
  { value: MEDIO_PAGO_PROVEEDOR.TRANSFERENCIA, label: "Transferencia" },
  { value: MEDIO_PAGO_PROVEEDOR.CHEQUE, label: "Cheque" },
  { value: MEDIO_PAGO_PROVEEDOR.ECHEQUE, label: "E-cheque" },
];

export const configPagoProveedorVacia = () => ({
  medio: "",
  codigoBanco: "",
  id_bancos_cuentas: "",
});

/**
 * Reconstruye la config por proveedor del paso 3 a partir del medioPago
 * ya guardado en una OP (edición de lote).
 */
export const configPagoDesdeMedioPago = (medioPago) => {
  if (medioPago?.transferencia?.activo) {
    const cuenta = medioPago.transferencia?.cuentasOrigen?.[0] ?? null;
    return {
      medio: MEDIO_PAGO_PROVEEDOR.TRANSFERENCIA,
      codigoBanco: cuenta?.codigoBanco || "",
      id_bancos_cuentas: cuenta?.id_bancos_cuentas || "",
    };
  }
  if (medioPago?.cheque?.activo) {
    const emitidos = medioPago.cheque?.emitidos ?? [];
    const recibidos = medioPago.cheque?.recibidos ?? [];
    const esEcheque = [...emitidos, ...recibidos].some(
      (c) => Number(c?.electronico) === 1
    );
    return {
      medio: esEcheque
        ? MEDIO_PAGO_PROVEEDOR.ECHEQUE
        : MEDIO_PAGO_PROVEEDOR.CHEQUE,
      codigoBanco: "",
      id_bancos_cuentas: "",
    };
  }
  return configPagoProveedorVacia();
};

export const esMedioTransferenciaProv = (medio) =>
  medio === MEDIO_PAGO_PROVEEDOR.TRANSFERENCIA;

export const esMedioChequeProv = (medio) =>
  medio === MEDIO_PAGO_PROVEEDOR.CHEQUE ||
  medio === MEDIO_PAGO_PROVEEDOR.ECHEQUE;

/** Transferencia al proveedor exige banco y CBU cargados en su ficha. */
export const proveedorPuedeTransferencia = (prov) => {
  const cbu = String(prov?.cbu || "").replace(/\s/g, "");
  const tieneBanco =
    (prov?.id_banco != null && String(prov.id_banco).trim() !== "") ||
    Boolean(String(prov?.banco_nombre || "").trim());
  return Boolean(cbu) && tieneBanco;
};

export const configPagoProveedorCompleta = (cfg, proveedor = null) => {
  if (!cfg?.medio) return true;
  if (esMedioTransferenciaProv(cfg.medio)) {
    if (proveedor && !proveedorPuedeTransferencia(proveedor)) return false;
    return Boolean(cfg.id_bancos_cuentas);
  }
  return esMedioChequeProv(cfg.medio);
};

/** True si las configs de cada CUIT son válidas (medio opcional; transferencia con cuenta). */
export const configsPagoSeleccionCompletas = (
  cuitsSeleccionados = [],
  configPorCuit = {},
  proveedoresPorCuit = {}
) => {
  if (!cuitsSeleccionados.length) return false;
  return cuitsSeleccionados.every((cuit) =>
    configPagoProveedorCompleta(
      configPorCuit[String(cuit)],
      proveedoresPorCuit[String(cuit)]
    )
  );
};

/** Clave de banco en el catálogo de cuentas activas (codigo / razón social). */
export const claveBancoCatalogo = (cuenta) =>
  cuenta?.codigo ?? cuenta?.razon_social ?? "";

export const etiquetaCuentaOrigen = (cuenta) => {
  if (!cuenta) return "";
  const banco = cuenta.razon_social || cuenta.codigo || "Banco";
  if (cuenta.nro_cuenta) {
    return `${banco} · ${cuenta.nro_cuenta}`;
  }
  if (cuenta.cbu) return `${banco} · ${cuenta.cbu}`;
  return String(banco);
};

/**
 * Arma el medioPago de una OP a partir de la config elegida para ese proveedor.
 * Conserva fecha/observaciones y cheques del medio global filtrados por CUIT.
 */
export const armarMedioPagoDesdeConfigProveedor = ({
  config,
  totalAPagar,
  medioPagoBase,
  cuentasBancarias = [],
  cuit = null,
}) => {
  const base = medioPagoBase || medioPagoInicial();
  const total = redondear2(Number(totalAPagar) || 0);
  const next = {
    ...medioPagoInicial(base.fechaPago || null),
    fechaPago: base.fechaPago || null,
    observaciones: base.observaciones || "",
    transferencia: { activo: false, cuentasOrigen: [] },
    cheque: { activo: false, recibidos: [], emitidos: [] },
  };

  if (!requiereMediosPago(total)) {
    return next;
  }

  if (esMedioTransferenciaProv(config?.medio)) {
    const idCuenta = config.id_bancos_cuentas;
    const cuenta = (cuentasBancarias || []).find(
      (c) => String(c.id_bancos_cuentas) === String(idCuenta)
    );
    next.transferencia = {
      activo: true,
      cuentasOrigen: [
        {
          key: `cuenta-cfg-${idCuenta}`,
          codigoBanco: config.codigoBanco || claveBancoCatalogo(cuenta),
          id_bancos_cuentas: idCuenta,
          importe: total > 0 ? String(total) : "",
        },
      ],
    };
    return next;
  }

  if (esMedioChequeProv(config?.medio)) {
    const { recibidos, emitidos } = chequesDelMedio(base, cuit);
    next.cheque = {
      activo: true,
      recibidos,
      emitidos,
      variante: config.medio,
    };
    return next;
  }

  return next;
};

/**
 * Agrega configs por proveedor al estado multi-medio del wizard (paso Medio).
 * Transferencias: una fila por cuenta origen con la suma de esos proveedores.
 * Cheques: activa el medio si algún proveedor eligió cheque/e-cheque.
 */
export const armarMedioPagoAgregadoDesdeConfigs = ({
  configPorCuit = {},
  proveedoresConTotal = [],
  medioPagoBase,
  cuentasBancarias = [],
}) => {
  const base = medioPagoBase || medioPagoInicial();
  const next = {
    ...medioPagoInicial(base.fechaPago || null),
    fechaPago: base.fechaPago || null,
    observaciones: base.observaciones || "",
    // Conservar otros medios cargados manualmente (efectivo, tarjeta, etc.).
    efectivo: { ...(base.efectivo || { activo: false, importe: "" }) },
    compensacion: { ...(base.compensacion || {}) },
    debitoAutomatico: { ...(base.debitoAutomatico || {}) },
    tarjetaCredito: { ...(base.tarjetaCredito || {}) },
    ajuste: { ...(base.ajuste || {}) },
    transferencia: { activo: false, cuentasOrigen: [] },
    cheque: {
      activo: false,
      recibidos: [...(base.cheque?.recibidos ?? [])],
      emitidos: [...(base.cheque?.emitidos ?? [])],
    },
  };

  const porCuenta = new Map();
  let hayCheque = false;
  let totalTransferencia = 0;

  proveedoresConTotal.forEach((prov) => {
    const cuit = String(prov?.cuit || "");
    const cfg = configPorCuit[cuit];
    const total = redondear2(Number(prov?.total) || 0);
    if (!cfg?.medio || total <= 0) return;

    if (esMedioChequeProv(cfg.medio)) {
      hayCheque = true;
      return;
    }

    if (!esMedioTransferenciaProv(cfg.medio) || !cfg.id_bancos_cuentas) return;

    const id = String(cfg.id_bancos_cuentas);
    const prev = porCuenta.get(id) || {
      codigoBanco: cfg.codigoBanco || "",
      id_bancos_cuentas: cfg.id_bancos_cuentas,
      importe: 0,
    };
    prev.importe = redondear2(prev.importe + total);
    if (!prev.codigoBanco) {
      const cuenta = (cuentasBancarias || []).find(
        (c) => String(c.id_bancos_cuentas) === id
      );
      prev.codigoBanco = claveBancoCatalogo(cuenta);
    }
    porCuenta.set(id, prev);
    totalTransferencia = redondear2(totalTransferencia + total);
  });

  if (porCuenta.size > 0) {
    next.transferencia = {
      activo: true,
      cuentasOrigen: Array.from(porCuenta.values()).map((fila, idx) => ({
        key: `cuenta-agg-${fila.id_bancos_cuentas}-${idx}`,
        codigoBanco: fila.codigoBanco,
        id_bancos_cuentas: fila.id_bancos_cuentas,
        importe: String(fila.importe),
      })),
    };
  }

  if (hayCheque || (base.cheque?.recibidos?.length || base.cheque?.emitidos?.length)) {
    next.cheque = {
      ...next.cheque,
      activo: true,
    };
  }

  // Si no hubo configs útiles, conservar medio base (edición / flujo viejo).
  if (porCuenta.size === 0 && !hayCheque) {
    return {
      ...base,
      transferencia: {
        activo: Boolean(base.transferencia?.activo),
        cuentasOrigen: [...(base.transferencia?.cuentasOrigen ?? [])],
      },
    };
  }

  void totalTransferencia;
  return next;
};

/**
 * Agrupa la propuesta de pago por banco (transferencias) y por proveedor (cheques).
 * Sirve para el resumen del paso Medio / Confirmación.
 */
export const agruparPropuestaPago = ({
  configPorCuit = {},
  proveedoresConTotal = [],
  cuentasBancarias = [],
}) => {
  const transferenciasMap = new Map();
  const cheques = [];
  const detalleProveedores = [];

  proveedoresConTotal.forEach((prov) => {
    const cuit = String(prov?.cuit || "");
    const cfg = configPorCuit[cuit] || configPagoProveedorVacia();
    const total = redondear2(Number(prov?.total) || 0);
    const razon = prov?.razon_social || "Sin razón social";

    detalleProveedores.push({
      cuit,
      razon_social: razon,
      total,
      medio: cfg.medio || "",
      codigoBanco: cfg.codigoBanco || "",
      id_bancos_cuentas: cfg.id_bancos_cuentas || "",
      completa: configPagoProveedorCompleta(cfg, prov),
      puedeTransferencia: proveedorPuedeTransferencia(prov),
    });

    if (!cfg.medio || total <= 0) return;

    if (esMedioChequeProv(cfg.medio)) {
      cheques.push({
        cuit,
        razon_social: razon,
        medio: cfg.medio,
        importe: total,
      });
      return;
    }

    if (!esMedioTransferenciaProv(cfg.medio) || !cfg.id_bancos_cuentas) return;

    const id = String(cfg.id_bancos_cuentas);
    const cuenta = (cuentasBancarias || []).find(
      (c) => String(c.id_bancos_cuentas) === id
    );
    const bancoNombre =
      cuenta?.razon_social ||
      cfg.codigoBanco ||
      (cuenta ? claveBancoCatalogo(cuenta) : "Banco");

    const prev = transferenciasMap.get(id) || {
      id_bancos_cuentas: cfg.id_bancos_cuentas,
      codigoBanco: cfg.codigoBanco || claveBancoCatalogo(cuenta),
      banco_nombre: bancoNombre,
      nro_cuenta: cuenta?.nro_cuenta || "",
      importe: 0,
      proveedores: [],
    };
    prev.importe = redondear2(prev.importe + total);
    prev.proveedores.push({ cuit, razon_social: razon, importe: total });
    transferenciasMap.set(id, prev);
  });

  const etiquetaMedio = (medio) =>
    OPCIONES_MEDIO_PAGO_PROVEEDOR.find((o) => o.value === medio)?.label ||
    medio ||
    "—";

  return {
    transferencias: Array.from(transferenciasMap.values()),
    cheques,
    detalleProveedores,
    etiquetaMedio,
    totalTransferencias: redondear2(
      Array.from(transferenciasMap.values()).reduce((a, t) => a + t.importe, 0)
    ),
    totalCheques: redondear2(cheques.reduce((a, c) => a + c.importe, 0)),
  };
};

const soloDigitos = (valor) => String(valor || "").replace(/\D/g, "");

/**
 * Cheques del medio, opcionalmente filtrados por el CUIT al que se asignaron.
 * Sin CUIT devuelve todos los cargados.
 */
export const chequesDelMedio = (medioPago, cuit = null) => {
  if (!medioPago?.cheque?.activo) return { recibidos: [], emitidos: [] };

  const cuitFiltro = cuit ? soloDigitos(cuit) : null;
  const coincide = (item) =>
    !cuitFiltro || soloDigitos(item?.cuit_asignado) === cuitFiltro;

  return {
    recibidos: (medioPago.cheque.recibidos ?? []).filter(coincide),
    emitidos: (medioPago.cheque.emitidos ?? []).filter(coincide),
  };
};

export const importeChequesMedio = (medioPago, cuit = null) => {
  const { recibidos, emitidos } = chequesDelMedio(medioPago, cuit);
  const suma = [...recibidos, ...emitidos].reduce(
    (acc, cheque) => acc + parseImporteMedio(cheque?.importe),
    0
  );
  return redondear2(suma);
};

export const cantidadChequesMedio = (medioPago, cuit = null) => {
  const { recibidos, emitidos } = chequesDelMedio(medioPago, cuit);
  return recibidos.length + emitidos.length;
};

/** Fila para el estado del wizard a partir de un cheque del listado de cartera. */
export const chequeRecibidoAFila = (cheque, cuitAsignado = "") => ({
  id: cheque?.id_instrumento_bancario ?? cheque?.id,
  nro_cheque: cheque?.nro_cheque ?? "",
  id_cheque: cheque?.id_cheque ?? "",
  banco_emisor: cheque?.banco_emisor ?? "",
  importe: Number(cheque?.importe) || 0,
  fecha_pago: cheque?.fecha_pago ?? null,
  recibido_de: cheque?.recibido_de ?? "",
  clausula: cheque?.clausula ?? "",
  electronico: Number(cheque?.electronico) === 1 ? 1 : 0,
  cuit_asignado: soloDigitos(cuitAsignado),
  ...camposOrigenCheque(cheque, ORIGEN_CHEQUE.TERCERO),
});

/** Fila vacía para el formulario de cheque emitido. */
export const chequeEmitidoInicial = (cuitAsignado = "") => ({
  key: `emi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  nro_cheque: "",
  banco_emisor: "",
  importe: "",
  fecha_emision: "",
  fecha_pago: "",
  clausula: "A la orden",
  electronico: 0,
  cuit_asignado: soloDigitos(cuitAsignado),
  ...camposOrigenCheque(null, ORIGEN_CHEQUE.PROPIO),
});

/** Etiqueta legible de un cheque para desgloses y tablas. */
export const etiquetaChequeFila = (cheque, origen = "") => {
  const partes = [];
  if (cheque?.nro_cheque) partes.push(`N° ${cheque.nro_cheque}`);
  if (cheque?.banco_emisor) partes.push(cheque.banco_emisor);

  const fecha = fechaAApi(cheque?.fecha_pago);
  if (fecha) {
    const [anio, mes, dia] = fecha.split("-");
    partes.push(`vto. ${dia}/${mes}/${anio}`);
  }

  const detalle = partes.join(" · ") || "Cheque";
  const origenLabel =
    origen || String(cheque?.origen_cheque_label || "").trim();
  return origenLabel ? `${origenLabel}: ${detalle}` : detalle;
};

export const importeTransferenciaMedio = (medioPago) => {
  if (!medioPago?.transferencia?.activo) return 0;
  return redondear2(sumarImportesCuentas(medioPago.transferencia.cuentasOrigen));
};

/** Importe en efectivo asignado a un proveedor (multi-OP) o el global (un solo proveedor). */
export const importeEfectivoProveedor = (medioPago, cuit = null) => {
  if (!medioPago?.efectivo?.activo) return 0;
  const porProv = medioPago.efectivo.porProveedor;
  if (cuit && porProv && typeof porProv === "object") {
    return parseImporteMedio(porProv[soloDigitos(cuit)]);
  }
  if (porProv && typeof porProv === "object" && Object.keys(porProv).length > 0) {
    return 0;
  }
  return parseImporteMedio(medioPago.efectivo.importe);
};

/** Suma total de efectivo (global o suma de asignaciones por proveedor). */
export const sumaEfectivoMedio = (medioPago) => {
  if (!medioPago?.efectivo?.activo) return 0;
  const porProv = medioPago.efectivo.porProveedor;
  if (porProv && typeof porProv === "object" && Object.keys(porProv).length > 0) {
    return redondear2(
      Object.values(porProv).reduce(
        (acc, valor) => acc + parseImporteMedio(valor),
        0
      )
    );
  }
  return parseImporteMedio(medioPago.efectivo.importe);
};

/**
 * Importe del medio definido en la propuesta (p. ej. transferencia = total del proveedor).
 * Cheque/e-cheq no suma hasta que se seleccionen cheques concretos.
 */
export const importeMedioPropuestaProveedor = (config, totalProv) => {
  const total = redondear2(Number(totalProv) || 0);
  if (total <= 0 || !config?.medio) return 0;
  if (esMedioTransferenciaProv(config.medio)) return total;
  return 0;
};

/**
 * Medios ya asignados a un proveedor: cheques, efectivo y transferencia de la propuesta.
 */
export const sumaMediosAsignadosProveedor = (
  medioPago,
  cuit,
  totalProv,
  configPago = null
) => {
  const cuitDig = soloDigitos(cuit);
  let suma = importeChequesMedio(medioPago, cuitDig);
  suma += importeEfectivoProveedor(medioPago, cuitDig);
  suma += importeMedioPropuestaProveedor(configPago, totalProv);
  return redondear2(suma);
};

/** Diferencia individual por proveedor (total a pagar − medios asignados). */
export const restanteMediosProveedor = (
  medioPago,
  cuit,
  totalProv,
  configPago = null
) =>
  redondear2(
    (Number(totalProv) || 0) -
      sumaMediosAsignadosProveedor(medioPago, cuit, totalProv, configPago)
  );

/** IDs de cheques de cartera ya cargados en el pool global (no reutilizables). */
export const idsChequesCarteraCargados = (medioPago) =>
  (medioPago?.cheque?.recibidos ?? [])
    .map((item) => item.id)
    .filter((id) => id != null)
    .map(String);

export const desgloseImportesMedios = (medioPago) => {
  const efectivo = medioPago?.efectivo?.activo ? sumaEfectivoMedio(medioPago) : 0;
  const transferencia = importeTransferenciaMedio(medioPago);
  const compensacion = medioPago?.compensacion?.activo
    ? parseImporteMedio(medioPago.compensacion.importe)
    : 0;
  const debitoAutomatico = medioPago?.debitoAutomatico?.activo
    ? parseImporteMedio(medioPago.debitoAutomatico.importe)
    : 0;
  const tarjetaCredito = medioPago?.tarjetaCredito?.activo
    ? parseImporteMedio(medioPago.tarjetaCredito.importe)
    : 0;
  const ajuste = medioPago?.ajuste?.activo
    ? parseImporteMedio(medioPago.ajuste.importe)
    : 0;

  return {
    efectivo: redondear2(efectivo),
    transferencia: redondear2(transferencia),
    compensacion: redondear2(compensacion),
    debitoAutomatico: redondear2(debitoAutomatico),
    tarjetaCredito: redondear2(tarjetaCredito),
    ajuste: redondear2(ajuste),
    cheque: importeChequesMedio(medioPago),
  };
};

export const sumaImportesMedios = (medioPago) => {
  const d = desgloseImportesMedios(medioPago);
  return redondear2(
    d.efectivo +
      d.transferencia +
      d.compensacion +
      d.debitoAutomatico +
      d.tarjetaCredito +
      d.ajuste +
      d.cheque
  );
};

export const restanteMediosVsTotal = (medioPago, totalAPagar) =>
  redondear2((Number(totalAPagar) || 0) - sumaImportesMedios(medioPago));

export const hayMedioActivo = (medioPago) =>
  Boolean(
    medioPago?.efectivo?.activo ||
      medioPago?.transferencia?.activo ||
      medioPago?.compensacion?.activo ||
      medioPago?.debitoAutomatico?.activo ||
      medioPago?.tarjetaCredito?.activo ||
      medioPago?.ajuste?.activo ||
      medioPago?.cheque?.activo
  );

const cuentasOrigenValidas = (cuentasOrigen = []) => {
  const cuentas = cuentasOrigen ?? [];
  if (!cuentas.length) return false;
  const ids = new Set();
  for (const c of cuentas) {
    if (!c.id_bancos_cuentas) return false;
    const id = String(c.id_bancos_cuentas);
    if (ids.has(id)) return false;
    const imp = Number(c.importe);
    if (!Number.isFinite(imp) || imp <= 0) return false;
    ids.add(id);
  }
  return true;
};

const FORMATO_FECHA_API = /^\d{4}-\d{2}-\d{2}$/;

/** Normaliza una fecha a YYYY-MM-DD. Acepta string, Date o moment. */
export const fechaAApi = (valor) => {
  if (!valor) return "";
  if (typeof valor === "string") {
    const match = valor.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : "";
  }

  const fecha =
    typeof valor?.toDate === "function"
      ? valor.toDate()
      : valor instanceof Date
        ? valor
        : null;
  if (!fecha || Number.isNaN(fecha.getTime())) return "";

  const mes = String(fecha.getMonth() + 1).padStart(2, "0");
  const dia = String(fecha.getDate()).padStart(2, "0");
  return `${fecha.getFullYear()}-${mes}-${dia}`;
};

/** Moment valid para DatePicker MUI (AdapterMoment exige moment, no string). */
export const fechaAMoment = (valor) => {
  if (!valor) return null;
  if (moment.isMoment(valor)) {
    return valor.isValid() ? valor : null;
  }
  const m = moment(
    valor,
    [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "YYYY-MM-DD HH:mm:ss"],
    true
  );
  if (m.isValid()) return m;
  const mLoose = moment(valor);
  return mLoose.isValid() ? mLoose : null;
};

const chequeEmitidoValido = (cheque) => {
  if (parseImporteMedio(cheque?.importe) <= 0) return false;
  if (!String(cheque?.nro_cheque || "").trim()) return false;
  if (!String(cheque?.banco_emisor || "").trim()) return false;
  if (!FORMATO_FECHA_API.test(fechaAApi(cheque?.fecha_emision) || "")) return false;
  if (!FORMATO_FECHA_API.test(fechaAApi(cheque?.fecha_pago) || "")) return false;
  return true;
};

/**
 * Valida el medio cheque. `proveedores` (lista de CUITs) se usa para exigir la
 * asignación cuando la selección genera más de una OP.
 */
export const validarChequeMedio = (medioPago, proveedores = []) => {
  if (!medioPago?.cheque?.activo) return true;

  const { recibidos, emitidos } = chequesDelMedio(medioPago);
  if (recibidos.length + emitidos.length === 0) return false;

  const ids = new Set();
  for (const cheque of recibidos) {
    if (cheque?.id == null) return false;
    const id = String(cheque.id);
    if (ids.has(id)) return false;
    ids.add(id);
    if (parseImporteMedio(cheque.importe) <= 0) return false;
  }

  for (const cheque of emitidos) {
    if (!chequeEmitidoValido(cheque)) return false;
  }

  const cuits = (proveedores || [])
    .map((prov) => soloDigitos(prov?.cuit ?? prov))
    .filter(Boolean);

  if (cuits.length > 1) {
    const permitidos = new Set(cuits);
    for (const cheque of [...recibidos, ...emitidos]) {
      const asignado = soloDigitos(cheque?.cuit_asignado);
      if (!asignado || !permitidos.has(asignado)) return false;
    }

    // Un cheque es indivisible: no puede exceder el total de su proveedor.
    for (const prov of proveedores) {
      const totalProv = Number(prov?.total);
      if (!Number.isFinite(totalProv) || totalProv <= 0) continue;
      if (importeChequesMedio(medioPago, prov?.cuit) > totalProv + TOLERANCIA_MEDIOS) {
        return false;
      }
    }
  }

  return true;
};

/** Valida el estado multi-medio contra el total a pagar. */
export const validarMedioPagoMulti = (
  medioPago,
  totalAPagar,
  proveedores = [],
  configPorCuit = null
) => {
  if (!medioPago?.fechaPago) return false;

  const total = Number(totalAPagar) || 0;

  // Sin importe neto (retenciones que absorben el pago) no se cargan medios.
  if (!requiereMediosPago(total)) return !hayMedioActivo(medioPago);

  if (!hayMedioActivo(medioPago)) return false;

  const desglose = desgloseImportesMedios(medioPago);

  if (medioPago.efectivo?.activo && desglose.efectivo <= 0) return false;

  if (medioPago.transferencia?.activo) {
    if (!cuentasOrigenValidas(medioPago.transferencia.cuentasOrigen)) return false;
    if (desglose.transferencia <= 0) return false;
  }

  if (medioPago.compensacion?.activo) {
    if (desglose.compensacion <= 0) return false;
    const saldo = Number(medioPago.compensacion.saldoDisponible);
    if (!Number.isFinite(saldo) || saldo <= 0) return false;
    if (desglose.compensacion > saldo + TOLERANCIA_MEDIOS) return false;
  }

  if (medioPago.debitoAutomatico?.activo) {
    if (desglose.debitoAutomatico <= 0) return false;
    if (String(medioPago.debitoAutomatico.ultimos4 || "").replace(/\D/g, "").length < 4) {
      return false;
    }
  }

  if (medioPago.tarjetaCredito?.activo) {
    if (desglose.tarjetaCredito <= 0) return false;
    if (String(medioPago.tarjetaCredito.ultimos4 || "").replace(/\D/g, "").length < 4) {
      return false;
    }
  }

  if (medioPago.ajuste?.activo) {
    if (desglose.ajuste <= 0) return false;
    const descripcion = String(medioPago.ajuste.descripcion || "").trim();
    if (!descripcion) return false;
    const concepto =
      medioPago.ajuste.concepto === CONCEPTOS_AJUSTE.internos
        ? CONCEPTOS_AJUSTE.internos
        : CONCEPTOS_AJUSTE.varios;
    if (
      concepto === CONCEPTOS_AJUSTE.varios &&
      desglose.ajuste > AJUSTE_AUTONOMIA_VARIOS + TOLERANCIA_MEDIOS
    ) {
      return false;
    }
  }

  if (medioPago.cheque?.activo) {
    if (desglose.cheque <= 0) return false;
    if (!validarChequeMedio(medioPago, proveedores)) return false;
  }

  if (proveedores.length > 1) {
    for (const prov of proveedores) {
      const totalProv = Number(prov?.total) || 0;
      if (totalProv <= 0) continue;
      const cuitDig = soloDigitos(prov?.cuit);
      const cfg = configPorCuit?.[cuitDig] ?? configPorCuit?.[prov?.cuit] ?? null;
      const asignadoProv = sumaMediosAsignadosProveedor(
        medioPago,
        prov.cuit,
        totalProv,
        cfg
      );
      if (asignadoProv > totalProv + TOLERANCIA_MEDIOS) return false;
    }
  }

  return Math.abs(sumaImportesMedios(medioPago) - total) < TOLERANCIA_MEDIOS;
};

const armarCuentasTransferenciaBody = (cuentasOrigenDetalle = []) => {
  const cuentas = cuentasOrigenDetalle
    .filter((f) => f.id_bancos_cuentas)
    .map((fila) => {
      const cuenta = fila.cuenta ?? {};
      const id_banco =
        cuenta.id_banco != null
          ? Number(cuenta.id_banco)
          : Number.isFinite(Number(fila.codigoBanco))
            ? Number(fila.codigoBanco)
            : null;

      if (!Number.isFinite(id_banco)) {
        throw new Error(
          "No se pudo determinar el banco de origen de una de las cuentas seleccionadas"
        );
      }

      const importe = Number(fila.importe);
      if (!Number.isFinite(importe) || importe <= 0) {
        throw new Error("Indique un importe válido en cada cuenta origen");
      }

      return {
        id_banco,
        id_bancos_cuentas: Number(fila.id_bancos_cuentas),
        importe,
      };
    });

  if (!cuentas.length) {
    throw new Error("Seleccioná al menos una cuenta origen para la transferencia");
  }

  return cuentas;
};

/**
 * Arma el medio { tipo: "cheque" } del body. Los recibidos viajan solo con id:
 * el back toma su importe completo desde la base.
 */
const armarMedioChequeBody = (medioPago, cuit = null) => {
  const { recibidos, emitidos } = chequesDelMedio(medioPago, cuit);

  if (recibidos.length + emitidos.length === 0) {
    throw new Error(
      "Seleccioná al menos un cheque de cartera o cargá un cheque emitido"
    );
  }

  const ids = new Set();
  const chequesRecibidos = recibidos.map((cheque) => {
    const id = Number(cheque?.id);
    if (!Number.isFinite(id) || id <= 0) {
      throw new Error("Uno de los cheques de cartera no tiene identificador");
    }
    if (ids.has(id)) {
      throw new Error(
        `El cheque ${cheque.nro_cheque || id} está repetido en la selección`
      );
    }
    ids.add(id);
    return { id };
  });

  const chequesEmitidos = emitidos.map((cheque) => {
    const importe = parseImporteMedio(cheque?.importe);
    if (importe <= 0) {
      throw new Error("Indique el importe de cada cheque emitido");
    }

    const nroCheque = String(cheque?.nro_cheque || "").trim();
    const bancoEmisor = String(cheque?.banco_emisor || "").trim();
    if (!nroCheque || !bancoEmisor) {
      throw new Error("Indique número y banco de cada cheque emitido");
    }

    const fechaEmision = fechaAApi(cheque?.fecha_emision);
    const fechaPago = fechaAApi(cheque?.fecha_pago);
    if (!fechaEmision || !fechaPago) {
      throw new Error(
        `Indique fecha de emisión y de pago del cheque ${nroCheque}`
      );
    }

    const clausula = String(cheque?.clausula || "").trim();

    return {
      nro_cheque: nroCheque,
      banco_emisor: bancoEmisor,
      importe,
      fecha_emision: fechaEmision,
      fecha_pago: fechaPago,
      ...(clausula ? { clausula } : {}),
      electronico: Number(cheque?.electronico) === 1 ? 1 : 0,
    };
  });

  return {
    tipo: "cheque",
    importe: importeChequesMedio(medioPago, cuit),
    cheques_recibidos: chequesRecibidos,
    cheques_emitidos: chequesEmitidos,
  };
};

/**
 * Arma el array medios_pago para el body de generar / adelanto.
 * fecha_pago y observaciones van a nivel root del body.
 */
export const armarMediosPagoBody = ({
  medioPago,
  cuentasOrigenDetalle = [],
  cuit = null,
}) => {
  if (!hayMedioActivo(medioPago)) {
    throw new Error("Seleccioná al menos un medio de pago");
  }

  const desglose = desgloseImportesMedios(medioPago);
  const medios = [];

  if (medioPago.efectivo?.activo) {
    const importeEfectivo = cuit
      ? importeEfectivoProveedor(medioPago, cuit)
      : desglose.efectivo;
    if (importeEfectivo <= 0) {
      throw new Error("Indique el importe en efectivo");
    }
    medios.push({ tipo: "efectivo", importe: importeEfectivo });
  }

  if (medioPago.transferencia?.activo) {
    const cuentas = armarCuentasTransferenciaBody(cuentasOrigenDetalle);
    medios.push({
      tipo: "transferencia",
      importe: desglose.transferencia,
      cuentas,
    });
  }

  if (medioPago.compensacion?.activo) {
    if (desglose.compensacion <= 0) {
      throw new Error("Indique el importe de compensación");
    }
    const item = {
      tipo: "compensacion",
      importe: desglose.compensacion,
    };
    if (medioPago.compensacion.idCliente != null) {
      item.id_cliente = Number(medioPago.compensacion.idCliente);
    }
    medios.push(item);
  }

  if (medioPago.debitoAutomatico?.activo) {
    if (desglose.debitoAutomatico <= 0) {
      throw new Error("Indique el importe de débito automático");
    }
    const ultimos4 = String(medioPago.debitoAutomatico.ultimos4 || "")
      .replace(/\D/g, "")
      .slice(-4);
    if (ultimos4.length < 4) {
      throw new Error("Indique los últimos 4 dígitos de la tarjeta (débito automático)");
    }
    medios.push({
      tipo: "debito_automatico",
      importe: desglose.debitoAutomatico,
      ultimos4,
      ...(String(medioPago.debitoAutomatico.referencia || "").trim()
        ? { referencia: String(medioPago.debitoAutomatico.referencia).trim() }
        : {}),
    });
  }

  if (medioPago.tarjetaCredito?.activo) {
    if (desglose.tarjetaCredito <= 0) {
      throw new Error("Indique el importe de pago con tarjeta");
    }
    const ultimos4 = String(medioPago.tarjetaCredito.ultimos4 || "")
      .replace(/\D/g, "")
      .slice(-4);
    if (ultimos4.length < 4) {
      throw new Error("Indique los últimos 4 dígitos de la tarjeta de crédito");
    }
    medios.push({
      tipo: "tarjeta_credito",
      importe: desglose.tarjetaCredito,
      ultimos4,
      ...(String(medioPago.tarjetaCredito.banco || "").trim()
        ? { banco: String(medioPago.tarjetaCredito.banco).trim() }
        : {}),
      ...(String(medioPago.tarjetaCredito.referencia || "").trim()
        ? { referencia: String(medioPago.tarjetaCredito.referencia).trim() }
        : {}),
    });
  }

  if (medioPago.ajuste?.activo) {
    if (desglose.ajuste <= 0) {
      throw new Error("Indique el importe del ajuste");
    }
    const descripcion = String(medioPago.ajuste.descripcion || "").trim();
    if (!descripcion) {
      throw new Error("Indique la descripción del ajuste");
    }
    const concepto =
      medioPago.ajuste.concepto === CONCEPTOS_AJUSTE.internos
        ? CONCEPTOS_AJUSTE.internos
        : CONCEPTOS_AJUSTE.varios;
    if (
      concepto === CONCEPTOS_AJUSTE.varios &&
      desglose.ajuste > AJUSTE_AUTONOMIA_VARIOS + TOLERANCIA_MEDIOS
    ) {
      throw new Error(
        `Ajustes Varios: autonomía hasta ${formatMoneda(AJUSTE_AUTONOMIA_VARIOS)}`
      );
    }
    medios.push({
      tipo: "ajuste",
      importe: desglose.ajuste,
      concepto,
      descripcion: descripcion.slice(0, 250),
    });
  }

  // Con varios proveedores, un proveedor puede no tener cheques asignados: en su
  // OP el medio simplemente no aplica.
  if (medioPago.cheque?.activo) {
    if (!cuit || cantidadChequesMedio(medioPago, cuit) > 0) {
      medios.push(armarMedioChequeBody(medioPago, cuit));
    }
  }

  if (!medios.length) {
    throw new Error("Seleccioná al menos un medio de pago");
  }

  return medios;
};

/** Igual que armarMediosPagoBody pero devuelve [] si aún no hay medios válidos (preview). */
export const armarMediosPagoBodySafe = (params) => {
  try {
    return armarMediosPagoBody(params);
  } catch {
    return [];
  }
};

/**
 * Escala importes de medios activos para que sumen totalDestino.
 * Los cheques son indivisibles: se toman los del CUIT tal como están y el resto
 * de los medios se escala sobre el remanente.
 */
export const escalarMedioPagoATotal = (medioPago, totalDestino, cuit = null) => {
  const dest = redondear2(totalDestino);
  const importeCheques = importeChequesMedio(medioPago, cuit);
  const destResto = redondear2(dest - importeCheques);
  const suma = redondear2(sumaImportesMedios(medioPago) - importeChequesMedio(medioPago));

  const next = {
    ...medioPago,
    efectivo: { ...medioPago.efectivo },
    transferencia: {
      ...medioPago.transferencia,
      cuentasOrigen: [...(medioPago.transferencia?.cuentasOrigen ?? [])],
    },
    compensacion: { ...medioPago.compensacion },
    debitoAutomatico: { ...medioPago.debitoAutomatico },
    tarjetaCredito: { ...medioPago.tarjetaCredito },
    ajuste: { ...(medioPago.ajuste || {}) },
  };

  if (medioPago.cheque?.activo) {
    const { recibidos, emitidos } = chequesDelMedio(medioPago, cuit);
    next.cheque = { ...medioPago.cheque, recibidos, emitidos };
  }

  // Sin importe neto para esta OP no queda nada por distribuir.
  if (dest <= 0) {
    next.efectivo = { ...next.efectivo, activo: false, importe: "" };
    next.transferencia = { ...next.transferencia, activo: false, cuentasOrigen: [] };
    next.compensacion = { ...next.compensacion, activo: false, importe: "" };
    next.debitoAutomatico = { ...next.debitoAutomatico, activo: false, importe: "" };
    next.tarjetaCredito = { ...next.tarjetaCredito, activo: false, importe: "" };
    next.ajuste = { ...next.ajuste, activo: false, importe: "" };
    next.cheque = { ...next.cheque, activo: false, recibidos: [], emitidos: [] };
    return next;
  }

  if (suma <= 0) return next;

  // Los cheques cubren todo el total de esta OP: el resto de los medios no aplica.
  if (destResto <= 0) {
    next.efectivo = { ...next.efectivo, activo: false, importe: "" };
    next.transferencia = { ...next.transferencia, activo: false, cuentasOrigen: [] };
    next.compensacion = { ...next.compensacion, activo: false, importe: "" };
    next.debitoAutomatico = { ...next.debitoAutomatico, activo: false, importe: "" };
    next.tarjetaCredito = { ...next.tarjetaCredito, activo: false, importe: "" };
    next.ajuste = { ...next.ajuste, activo: false, importe: "" };
    return next;
  }

  const ratio = destResto / suma;

  if (next.efectivo.activo) {
    const porProv = next.efectivo.porProveedor;
    if (porProv && typeof porProv === "object" && Object.keys(porProv).length > 0) {
      const escalado = {};
      Object.entries(porProv).forEach(([cuitKey, valor]) => {
        escalado[cuitKey] = redondear2(parseImporteMedio(valor) * ratio);
      });
      next.efectivo.porProveedor = escalado;
      next.efectivo.importe = "";
    } else {
      next.efectivo.importe = redondear2(parseImporteMedio(next.efectivo.importe) * ratio);
    }
  }
  if (next.compensacion.activo) {
    next.compensacion.importe = redondear2(
      parseImporteMedio(next.compensacion.importe) * ratio
    );
  }
  if (next.debitoAutomatico.activo) {
    next.debitoAutomatico.importe = redondear2(
      parseImporteMedio(next.debitoAutomatico.importe) * ratio
    );
  }
  if (next.tarjetaCredito.activo) {
    next.tarjetaCredito.importe = redondear2(
      parseImporteMedio(next.tarjetaCredito.importe) * ratio
    );
  }
  if (next.ajuste?.activo) {
    next.ajuste.importe = redondear2(parseImporteMedio(next.ajuste.importe) * ratio);
  }
  if (next.transferencia.activo) {
    const destTransf = redondear2(importeTransferenciaMedio(medioPago) * ratio);
    next.transferencia.cuentasOrigen = ajustarCuentasATotal(
      next.transferencia.cuentasOrigen,
      destTransf
    );
  }

  return next;
};

/**
 * Arma el body para POST /orden-pago/proveedores/:cuit/generar.
 */
export const buildGenerarOrdenPagoBody = ({
  comprobantes,
  fechaPago,
  observaciones = "",
  medioPago,
  cuentasOrigenDetalle = [],
  user,
  email,
  cuit = null,
  totalAPagar = null,
  confirmarAdvertencias = false,
  retenciones = null,
}) => {
  if (!Array.isArray(comprobantes) || comprobantes.length === 0) {
    throw new Error("No hay comprobantes seleccionados");
  }
  if (!fechaPago) {
    throw new Error("Indique la fecha de pago");
  }

  const emailTrim = String(email || "").trim();
  const correoProveedor = emailTrim ? { email: emailTrim } : {};
  const observacionesTrim = String(observaciones || "").trim();

  const sinImporteNeto =
    totalAPagar != null && !requiereMediosPago(totalAPagar);

  return {
    comprobantes,
    user: String(user || "").trim(),
    ...correoProveedor,
    fecha_pago: fechaPago,
    ...(observacionesTrim ? { observaciones: observacionesTrim } : {}),
    ...(confirmarAdvertencias ? { confirmar_advertencias: true } : {}),
    ...(retenciones ? { retenciones } : {}),
    medios_pago: sinImporteNeto
      ? []
      : armarMediosPagoBody({
          medioPago,
          cuentasOrigenDetalle,
          cuit,
        }),
  };
};

/**
 * Arma el body para POST /orden-pago/proveedores/:cuit/adelanto.
 */
export const buildAdelantoOrdenPagoBody = ({
  id_empresa,
  monto,
  motivo,
  fechaPago,
  observaciones = "",
  user,
  email,
  id_banco,
  id_bancos_cuentas,
}) => {
  const idEmpresa = Number(id_empresa);
  if (!Number.isFinite(idEmpresa) || idEmpresa <= 0) {
    throw new Error("id_empresa es obligatorio");
  }

  const montoNum = Number(monto);
  if (!Number.isFinite(montoNum) || montoNum <= 0) {
    throw new Error("El monto del adelanto debe ser mayor a 0");
  }

  const motivoTrim = String(motivo || "").trim();
  if (!motivoTrim) {
    throw new Error("El motivo del adelanto es obligatorio");
  }
  if (motivoTrim.length > 250) {
    throw new Error("El motivo no puede superar los 250 caracteres");
  }

  if (!fechaPago) {
    throw new Error("Indique la fecha de pago");
  }

  const idBanco = Number(id_banco);
  if (!Number.isFinite(idBanco) || idBanco <= 0) {
    throw new Error("Seleccioná el banco de origen");
  }

  const idBancosCuentas = Number(id_bancos_cuentas);
  if (!Number.isFinite(idBancosCuentas) || idBancosCuentas <= 0) {
    throw new Error("Seleccioná la cuenta de origen");
  }

  const emailTrim = String(email || "").trim();
  const correoProveedor = emailTrim ? { email: emailTrim } : {};
  const observacionesTrim = String(observaciones || "").trim();
  const importe = redondear2(montoNum);

  return {
    id_empresa: idEmpresa,
    monto: importe,
    motivo: motivoTrim,
    user: String(user || "").trim(),
    ...correoProveedor,
    fecha_pago: fechaPago,
    ...(observacionesTrim ? { observaciones: observacionesTrim } : {}),
    medios_pago: [
      {
        tipo: "transferencia",
        importe,
        cuentas: [
          {
            id_banco: idBanco,
            id_bancos_cuentas: idBancosCuentas,
            importe,
          },
        ],
      },
    ],
  };
};

/** @deprecated Usar validarMedioPagoMulti. Mantiene compatibilidad con suma de cuentas. */
export const validarCuentasOrigen = (cuentasOrigen, totalAPagar) => {
  const total = Number(totalAPagar) || 0;
  if (total <= 0) return false;
  if (!cuentasOrigenValidas(cuentasOrigen)) return false;
  const suma = sumarImportesCuentas(cuentasOrigen);
  return Math.abs(suma - total) < TOLERANCIA_MEDIOS;
};

const ETIQUETAS_TIPO_MEDIO = {
  efectivo: "Efectivo",
  efectivo_cheque: "Efectivo / Cheque",
  transferencia: "Transferencia",
  banco: "Banco",
  echeck: "eCheck",
  compensacion: "Compensación",
  debito_automatico: "Débito automático",
  tarjeta_credito: "Tarjeta de crédito",
  ajuste: "Ajuste",
  cheque: "Cheque",
};

export const etiquetaTipoMedio = (tipo, extra = {}) => {
  if (!tipo) return "—";
  if (tipo === "banco") {
    if (extra.tipo_bancario === "echeck") return "Banco · eCheck";
    if (extra.tipo_bancario === "transferencia") return "Banco · Transferencia";
    return "Banco";
  }
  if (tipo === "ajuste") {
    const conceptoLabel = etiquetaConceptoAjuste(extra.concepto);
    return conceptoLabel !== "Ajuste" ? `Ajuste · ${conceptoLabel}` : "Ajuste";
  }
  return ETIQUETAS_TIPO_MEDIO[tipo] || String(tipo);
};

/** Etiqueta para un medio único o un array de medios (API / listado). */
export const etiquetaMedioPagoApi = (medioOMedios) => {
  if (Array.isArray(medioOMedios)) {
    if (!medioOMedios.length) return "—";
    return medioOMedios
      .map((m) => etiquetaTipoMedio(m?.tipo, m))
      .filter(Boolean)
      .join(" + ");
  }
  if (!medioOMedios) return "—";
  return etiquetaTipoMedio(medioOMedios.tipo, medioOMedios);
};

/** Etiquetas legibles del estado del wizard (multi-medio). */
export const etiquetasMediosWizard = (medioPago) => {
  const items = [];
  if (medioPago?.efectivo?.activo) items.push("Efectivo");
  if (medioPago?.transferencia?.activo) items.push("Transferencia");
  if (medioPago?.compensacion?.activo) items.push("Compensación");
  if (medioPago?.debitoAutomatico?.activo) items.push("Débito automático");
  if (medioPago?.tarjetaCredito?.activo) items.push("Tarjeta de crédito");
  if (medioPago?.ajuste?.activo) {
    items.push(`Ajuste · ${etiquetaConceptoAjuste(medioPago.ajuste.concepto)}`);
  }
  if (medioPago?.cheque?.activo) items.push("Cheque");
  return items.length ? items.join(" + ") : "—";
};

/** Líneas de desglose para confirmación / documento. */
export const lineasDesgloseMedios = (medioPago, cuentasOrigenDetalle = []) => {
  const lineas = [];
  const d = desgloseImportesMedios(medioPago);

  if (medioPago?.efectivo?.activo && d.efectivo > 0) {
    lineas.push({ key: "efectivo", label: "Efectivo", importe: d.efectivo });
  }

  if (medioPago?.transferencia?.activo && d.transferencia > 0) {
    lineas.push({
      key: "transferencia",
      label: "Transferencia",
      importe: d.transferencia,
    });
    (cuentasOrigenDetalle || []).forEach((fila, i) => {
      if (!fila?.id_bancos_cuentas) return;
      lineas.push({
        key: `cta-${fila.key ?? i}`,
        label: `${fila.cuenta?.razon_social || "Banco"} — Cta. ${
          fila.cuenta?.nro_cuenta || "—"
        }`,
        importe: Number(fila.importe) || 0,
        secundaria: true,
      });
    });
  }

  if (medioPago?.compensacion?.activo && d.compensacion > 0) {
    lineas.push({
      key: "compensacion",
      label: "Compensación",
      importe: d.compensacion,
    });
  }

  if (medioPago?.debitoAutomatico?.activo && d.debitoAutomatico > 0) {
    const u4 = String(medioPago.debitoAutomatico.ultimos4 || "").slice(-4);
    lineas.push({
      key: "debito",
      label: u4 ? `Débito automático (····${u4})` : "Débito automático",
      importe: d.debitoAutomatico,
    });
  }

  if (medioPago?.tarjetaCredito?.activo && d.tarjetaCredito > 0) {
    const u4 = String(medioPago.tarjetaCredito.ultimos4 || "").slice(-4);
    lineas.push({
      key: "tarjeta",
      label: u4 ? `Tarjeta de crédito (····${u4})` : "Tarjeta de crédito",
      importe: d.tarjetaCredito,
    });
  }

  if (medioPago?.ajuste?.activo && d.ajuste > 0) {
    const concepto = etiquetaConceptoAjuste(medioPago.ajuste.concepto);
    const desc = String(medioPago.ajuste.descripcion || "").trim();
    lineas.push({
      key: "ajuste",
      label: desc ? `Ajuste · ${concepto}: ${desc}` : `Ajuste · ${concepto}`,
      importe: d.ajuste,
    });
  }

  if (medioPago?.cheque?.activo && d.cheque > 0) {
    lineas.push({ key: "cheque", label: "Cheque", importe: d.cheque });

    const { recibidos, emitidos } = chequesDelMedio(medioPago);
    recibidos.forEach((cheque, i) => {
      lineas.push({
        key: `cheque-rec-${cheque.id ?? i}`,
        label: etiquetaChequeFila(cheque),
        importe: Number(cheque.importe) || 0,
        secundaria: true,
      });
    });
    emitidos.forEach((cheque, i) => {
      lineas.push({
        key: `cheque-emi-${cheque.key ?? i}`,
        label: etiquetaChequeFila(cheque),
        importe: Number(cheque.importe) || 0,
        secundaria: true,
      });
    });
  }

  return lineas;
};

export const alicuotaRetencion = (item) => {
  if (item?.alicuota_efectiva != null) return item.alicuota_efectiva;
  if (item?.alicuota != null) return item.alicuota;
  const base = Number(item?.base);
  const importe = Number(item?.importe);
  if (base > 0 && importe >= 0) {
    return Math.round((importe / base) * 10000) / 100;
  }
  return null;
};

export const detalleRetencion = (item, formatMonedaFn = formatMoneda) => {
  const alicuota = alicuotaRetencion(item);
  if (alicuota != null && alicuota > 0) {
    return `${alicuota}% sobre ${formatMonedaFn(item.base)}`;
  }
  return item?.base != null ? `Sobre ${formatMonedaFn(item.base)}` : "";
};

const certificadosDesdeRetenciones = (retenciones, preview, empresaLabel, idEmpresa) => {
  if (!retenciones) return [];
  const lista = [];
  const prefijoEmpresa =
    empresaLabel && idEmpresa != null ? `${empresaLabel} · ` : "";
  const ganancias = retenciones.ganancias;

  if (ganancias) {
    const importeGan = Number(ganancias.importe ?? 0);
    const idBase =
      ganancias.id != null ? `ganancias-${ganancias.id}` : "ganancias";
    lista.push({
      id: idEmpresa != null ? `${idEmpresa}-${idBase}` : idBase,
      tipoDocumento: "Retención Ganancias",
      titulo: `${prefijoEmpresa}Certificado de retención — Ganancias`,
      empresa: empresaLabel,
      idEmpresa,
      provincia: null,
      certificado: ganancias.certificado || "Ganancias",
      aplica: ganancias.aplica != null ? Boolean(ganancias.aplica) : importeGan > 0,
      mensaje: ganancias.mensaje,
      base: ganancias.base ?? ganancias.base_imponible,
      importe: importeGan,
      alicuota: alicuotaRetencion(ganancias),
      nroCertificado: ganancias.nro_certificado,
      idRetencion: ganancias.id,
      condicion: preview?.proveedor?.condicion_ganancias,
      codigoPadron: preview?.proveedor?.codigo_ganancias_padron,
    });
  }

  (retenciones.iibb ?? []).forEach((item) => {
    const importe = Number(item.importe ?? 0);
    const idBase =
      item.id != null ? `iibb-${item.id}` : `iibb-${item.id_provincia}`;
    lista.push({
      id: idEmpresa != null ? `${idEmpresa}-${idBase}` : idBase,
      tipoDocumento: "Retención IIBB",
      titulo: `${prefijoEmpresa}Certificado de retención — IIBB ${item.provincia || ""}`.trim(),
      empresa: empresaLabel,
      idEmpresa,
      provincia: item.provincia,
      certificado: item.certificado || `IIBB ${item.provincia}`,
      aplica: importe > 0,
      mensaje: null,
      base: item.base ?? item.base_imponible,
      importe,
      alicuota: alicuotaRetencion(item),
      nroCertificado: item.nro_certificado,
      idRetencion: item.id,
      idProvincia: item.id_provincia,
    });
  });

  const iva = retenciones.iva;
  if (iva) {
    const importeIva = Number(iva.importe ?? 0);
    const idBase = iva.id != null ? `iva-${iva.id}` : "iva";
    lista.push({
      id: idEmpresa != null ? `${idEmpresa}-${idBase}` : idBase,
      tipoDocumento: "Retención IVA",
      titulo: `${prefijoEmpresa}Certificado de retención — IVA`.trim(),
      empresa: empresaLabel,
      idEmpresa,
      provincia: null,
      certificado: iva.certificado || "IVA",
      aplica: iva.aplica != null ? Boolean(iva.aplica) : importeIva > 0,
      mensaje: iva.mensaje,
      base: iva.base ?? iva.base_imponible,
      importe: importeIva,
      alicuota: alicuotaRetencion(iva),
      nroCertificado: iva.nro_certificado,
      idRetencion: iva.id,
    });
  }

  return lista;
};

/** Certificados de retención para el paso de confirmación (un documento por ítem) */
export const listarCertificadosRetencion = (preview) => {
  const grupos = Array.isArray(preview?.grupos) ? preview.grupos : [];
  if (preview?.multi_proveedor && grupos.length > 1) {
    return grupos.flatMap((grupo) => {
      const razon =
        grupo.razon_social || grupo.proveedor?.razon_social || grupo.cuit || "";
      const lista = certificadosDesdeRetenciones(
        grupo.retenciones,
        { ...preview, ...grupo, retenciones: grupo.retenciones },
        razon,
        null
      );
      return lista.map((cert) => ({
        ...cert,
        cuit: grupo.cuit,
        razon_social: razon,
        // Evitar colisión de keys entre proveedores.
        id: `${grupo.cuit}-${cert.id}`,
      }));
    });
  }

  if (!preview?.retenciones) return [];
  return certificadosDesdeRetenciones(preview.retenciones, preview, null, null);
};

/** Detalle unificado de OP (preview, generar, GET). */
export const obtenerDetalleOp = (preview, data) =>
  data?.detalle ?? preview?.detalle ?? null;

/** Excluye percepciones IIBB (van en retenciones, no en esta sección). */
export const esImpuestoIibbEnFactura = (item) => {
  if (String(item?.tipo || "").toLowerCase() === "iibb") return true;
  const desc = String(item?.descripcion || "").trim().toUpperCase();
  return (
    desc.startsWith("IIBB") ||
    desc.includes("ING.BRUTOS") ||
    desc.includes("ING BRUTOS")
  );
};

const filtrarOtrosImpuestosFactura = (lista) =>
  (Array.isArray(lista) ? lista : []).filter(
    (item) => !esImpuestoIibbEnFactura(item)
  );

/** Otros impuestos de factura (sin IIBB), informativos. */
export const obtenerImpuestosFactura = (preview, detalle) => {
  const fuentes = [
    detalle?.impuestos_factura,
    preview?.detalle?.impuestos_factura,
    preview?.resumen?.impuestos_factura,
    detalle?.resumen?.impuestos_factura,
    preview?.impuestos_factura,
  ];

  for (const lista of fuentes) {
    const filtrada = filtrarOtrosImpuestosFactura(lista);
    if (filtrada.length > 0) return filtrada;
  }
  return [];
};

export const obtenerTotalesOtrosImpuestosFactura = (preview, detalle) => {
  const fuentes = [
    detalle?.resumen,
    preview?.detalle?.resumen,
    preview?.resumen,
    detalle,
    preview,
  ];

  for (const src of fuentes) {
    if (!src) continue;
    const totalFactura = src.total_otros_impuestos_factura;
    const totalAportado = src.total_otros_impuestos_aportado;
    if (totalFactura != null || totalAportado != null) {
      return {
        total_factura: totalFactura,
        total_aportado: totalAportado,
      };
    }
  }
  return null;
};

export const textoImpuestoFactura = (item, formatMonedaFn = formatMoneda) => {
  if (item?.detalle) return item.detalle;

  const desc = item?.descripcion || "Impuesto";
  const factura = item?.importe_factura;
  const aportado = item?.importe_aportado;

  if (factura != null && aportado != null) {
    return `${desc}: factura ${formatMonedaFn(factura)} · en este pago ${formatMonedaFn(aportado)}`;
  }
  if (aportado != null) {
    return `${desc}: en este pago ${formatMonedaFn(aportado)}`;
  }
  return desc;
};

export const colorEstadoTipoPago = (tipoPago) => {
  switch (tipoPago) {
    case "total":
      return "success";
    case "parcial":
      return "warning";
    case "credito_total":
      return "info";
    case "credito_parcial":
      return "warning";
    default:
      return "default";
  }
};

export const mapImputacionAFilaComprobante = (imp) => {
  if (
    Boolean(imp?.es_adelanto) ||
    String(imp?.tipo_comprobante || "").toLowerCase() === "adelanto" ||
    (imp?.id_anticipo != null && imp.id_anticipo !== "")
  ) {
    return mapAdelantoAplicadoAFila(imp);
  }

  const importeMax = (() => {
    const n = (v) => (v != null && v !== "" ? Number(v) : NaN);
    const tope = n(imp.importe_maximo ?? imp.saldo_disponible);
    if (Number.isFinite(tope)) return Math.abs(tope);
    return null;
  })();

  const saldoPendiente =
    importeMax != null
      ? importeMax
      : imp.saldo_antes != null
        ? Number(imp.saldo_antes)
        : imp.saldo_pendiente != null
          ? Number(imp.saldo_pendiente)
          : Number(imp.importe_a_pagar ?? imp.importe);

  return {
    idcomprobante: imp.idcomprobante,
    tipo_comprobante: imp.tipo_comprobante,
    tipo_comprobante_abrev: imp.tipo_comprobante_abrev,
    nro_comprobante: imp.nro_comprobante,
    importe_a_pagar: Number(imp.importe_a_pagar ?? imp.importe),
    saldo_pendiente: saldoPendiente,
    importe_maximo: importeMax,
    saldo_disponible:
      imp.saldo_disponible != null
        ? Number(imp.saldo_disponible)
        : importeMax,
    total: imp.total ?? imp.total_comprobante,
    total_comprobante: imp.total_comprobante,
    saldo_antes: imp.saldo_antes,
    saldo_despues: imp.saldo_despues,
    saldo_restante: imp.saldo_restante,
    importe_pagado: imp.importe_pagado,
    tipo_aplicacion: imp.tipo_aplicacion,
    tipo_pago: imp.tipo_pago,
    tipo_pago_label: imp.tipo_pago_label,
    es_credito: imp.es_credito,
    neto_imponible_aportado: imp.neto_imponible_aportado,
    neto_imponible: imp.neto_imponible,
    confirmada: imp.confirmada,
    fecha_factura: imp.fecha_factura ?? null,
    fecha_vencimiento: imp.fecha_vencimiento ?? null,
    id_empresa: imp.id_empresa ?? null,
    nombre_empresa: imp.nombre_empresa ?? null,
  };
};

/**
 * Adelanto aplicado en una OP (GET /orden-pago/:id → adelantos_aplicados).
 * importe_maximo / saldo_disponible = tope editable al aprobar.
 */
export const mapAdelantoAplicadoAFila = (ad) => {
  const r2 = (x) => Math.round(Number(x) * 100) / 100;
  const n = (v) => (v != null && v !== "" ? Number(v) : NaN);

  const idAnticipo = ad.id_anticipo ?? ad.id ?? null;
  const montoAnticipo = n(ad.monto_anticipo ?? ad.monto ?? ad.total);
  const aplicadoAbs = (() => {
    const desdeNeg = n(ad.importe_a_pagar);
    if (Number.isFinite(desdeNeg)) return Math.abs(desdeNeg);
    const desdePos = n(ad.importe_aplicado ?? ad.importe);
    if (Number.isFinite(desdePos)) return Math.abs(desdePos);
    return 0;
  })();

  const topeAbs = (() => {
    const tope = n(ad.importe_maximo ?? ad.saldo_disponible);
    if (Number.isFinite(tope) && Math.abs(tope) > TOLERANCIA_IMPORTE) {
      return Math.abs(tope);
    }
    if (Number.isFinite(montoAnticipo) && montoAnticipo > TOLERANCIA_IMPORTE) {
      return Math.abs(montoAnticipo);
    }
    return aplicadoAbs > 0 ? aplicadoAbs : 0;
  })();

  const saldoSinAplicar = n(ad.saldo_sin_aplicar);

  return {
    id_anticipo: idAnticipo != null ? Number(idAnticipo) : null,
    id_orden_pago: ad.id_orden_pago != null ? Number(ad.id_orden_pago) : null,
    es_adelanto: true,
    es_credito: true,
    tipo_comprobante: ad.tipo_comprobante || "Adelanto",
    tipo_comprobante_abrev: ad.tipo_comprobante_abrev || "Adel",
    nro_comprobante:
      ad.nro_comprobante ||
      ad.nro_op ||
      (idAnticipo != null ? `#${idAnticipo}` : "—"),
    descripcion_breve: ad.motivo || ad.descripcion_breve || ad.descripcion || "",
    importe_a_pagar: r2(-aplicadoAbs),
    importe_aplicado: r2(aplicadoAbs),
    monto_anticipo: Number.isFinite(montoAnticipo) ? r2(montoAnticipo) : null,
    saldo_sin_aplicar: Number.isFinite(saldoSinAplicar)
      ? r2(saldoSinAplicar)
      : null,
    importe_maximo: r2(topeAbs),
    saldo_disponible: r2(
      Number.isFinite(n(ad.saldo_disponible))
        ? Math.abs(n(ad.saldo_disponible))
        : topeAbs
    ),
    // Tope firmado para inputs/validación de crédito.
    saldo_pendiente: r2(-topeAbs),
    total: Number.isFinite(montoAnticipo) ? r2(montoAnticipo) : r2(topeAbs),
    total_comprobante: Number.isFinite(montoAnticipo)
      ? r2(montoAnticipo)
      : r2(topeAbs),
    fecha_factura: ad.fecha ?? ad.fecha_factura ?? null,
    fecha_vencimiento: ad.fecha_vencimiento ?? null,
    id_empresa: ad.id_empresa ?? null,
    nombre_empresa: ad.nombre_empresa ?? null,
    razon_social: ad.razon_social ?? null,
    nro_cuit: ad.nro_cuit ?? ad.cuit ?? null,
    tipo_pago: ad.tipo_pago,
    tipo_pago_label: ad.tipo_pago_label,
  };
};

const mapMedioApiAWizard = (mediosApi, medioLegacy, op = {}, cuitProveedor = "") => {
  const medios = Array.isArray(mediosApi)
    ? mediosApi
    : medioLegacy && Object.keys(medioLegacy).length
      ? [medioLegacy]
      : [];

  const cuitOp = soloDigitos(
    cuitProveedor ||
      op?.nro_cuit ||
      op?.cuit ||
      medioLegacy?.cuit_asignado ||
      ""
  );

  const base = medioPagoInicial(null);
  base.fechaPago = fechaAMoment(
    medios[0]?.fecha_pago || medioLegacy?.fecha_pago || op.fecha || null
  );
  base.observaciones =
    medios[0]?.observaciones ||
    medioLegacy?.observaciones ||
    op.observaciones ||
    "";
  base.transferencia = { activo: false, cuentasOrigen: [] };

  if (!medios.length) {
    // Legacy: efectivo_cheque / banco
    const tipo = medioLegacy?.tipo || op.tipo_medio;
    const tipoBancario = medioLegacy?.tipo_bancario || op.tipo_bancario;
    if (tipo === "efectivo_cheque" || tipo === "efectivo") {
      base.efectivo = {
        activo: true,
        importe: String(medioLegacy?.importe ?? op.total_neto ?? ""),
      };
    } else if (tipo === "banco" && tipoBancario === "transferencia") {
      base.transferencia = { activo: true, cuentasOrigen: [] };
    } else if (tipo === "banco" && tipoBancario === "echeck") {
      base.efectivo = { activo: false, importe: "" };
      base.transferencia = { activo: false, cuentasOrigen: [] };
    }
    return base;
  }

  medios.forEach((m) => {
    const tipo = m?.tipo;
    const importe = m?.importe != null ? String(m.importe) : "";
    if (tipo === "efectivo" || tipo === "efectivo_cheque") {
      base.efectivo = { activo: true, importe };
    } else if (tipo === "transferencia" || (tipo === "banco" && m.tipo_bancario === "transferencia")) {
      base.transferencia = { activo: true, cuentasOrigen: [] };
    } else if (tipo === "compensacion") {
      base.compensacion = {
        ...base.compensacion,
        activo: true,
        importe,
        idCliente: m.id_cliente ?? null,
      };
    } else if (tipo === "debito_automatico") {
      base.debitoAutomatico = {
        activo: true,
        importe,
        ultimos4: m.ultimos4 || "",
        referencia: m.referencia || "",
      };
    } else if (tipo === "tarjeta_credito") {
      base.tarjetaCredito = {
        activo: true,
        importe,
        ultimos4: m.ultimos4 || "",
        banco: m.banco || "",
        referencia: m.referencia || "",
      };
    } else if (tipo === "ajuste") {
      base.ajuste = {
        activo: true,
        importe,
        concepto:
          m.concepto === CONCEPTOS_AJUSTE.internos
            ? CONCEPTOS_AJUSTE.internos
            : CONCEPTOS_AJUSTE.varios,
        descripcion: m.descripcion || "",
      };
    } else if (tipo === "cheque") {
      const recibidos = Array.isArray(m.cheques_recibidos)
        ? m.cheques_recibidos
        : [];
      const emitidos = Array.isArray(m.cheques_emitidos)
        ? m.cheques_emitidos
        : [];
      base.cheque = {
        activo: true,
        recibidos: recibidos.map((cheque) =>
          chequeRecibidoAFila(
            cheque,
            soloDigitos(cheque?.cuit_asignado || cheque?.cuit || cuitOp)
          )
        ),
        emitidos: emitidos.map((cheque, i) => ({
          key: cheque?.id ?? `emi-api-${i}`,
          nro_cheque: cheque?.nro_cheque ?? "",
          banco_emisor: cheque?.banco_emisor ?? "",
          importe: Number(cheque?.importe) || 0,
          fecha_emision: fechaAApi(cheque?.fecha_emision),
          fecha_pago: fechaAApi(cheque?.fecha_pago),
          clausula: cheque?.clausula ?? "",
          electronico: Number(cheque?.electronico) === 1 ? 1 : 0,
          cuit_asignado: soloDigitos(
            cheque?.cuit_asignado || cheque?.cuit || cuitOp
          ),
          ...camposOrigenCheque(cheque, ORIGEN_CHEQUE.PROPIO),
        })),
      };
    }
  });

  return base;
};

const mapCuentasDesdeMedios = (mediosApi, medioLegacy) => {
  const medios = Array.isArray(mediosApi)
    ? mediosApi
    : medioLegacy
      ? [medioLegacy]
      : [];
  const cuentasFuente =
    medios.find((m) => Array.isArray(m?.cuentas) && m.cuentas.length)?.cuentas ??
    medioLegacy?.cuentas ??
    [];

  return cuentasFuente.map((cuenta, index) => ({
    key: cuenta.id ?? index,
    id_bancos_cuentas: cuenta.id_bancos_cuentas,
    importe: Number(cuenta.importe),
    cuenta: {
      id_banco: cuenta.id_banco,
      razon_social: cuenta.banco_nombre || `Banco ${cuenta.id_banco ?? ""}`.trim(),
      nro_cuenta: cuenta.nro_cuenta,
      cbu: cuenta.cbu_origen,
    },
  }));
};

/** Adapta GET /orden-pago/:id (o respuesta de generar) al shape del wizard. */
export const mapDetalleOrdenPagoResponse = (data) => {
  const op = data?.orden_pago ?? {};
  const medio = data?.medio_pago ?? {};
  const mediosApi = data?.medios_pago ?? op?.medios_pago ?? null;
  const imputaciones = Array.isArray(data?.imputaciones) ? data.imputaciones : [];
  const comprobantesApi = Array.isArray(data?.comprobantes) ? data.comprobantes : [];
  const adelantosAplicadosApi = Array.isArray(data?.adelantos_aplicados)
    ? data.adelantos_aplicados
    : [];
  const retencionesApi = data?.retenciones ?? {};
  const resumenApi = data?.resumen ?? {};
  const proveedorApi = data?.proveedor ?? {};

  const compsBase =
    imputaciones.length > 0
      ? imputaciones.map(mapImputacionAFilaComprobante)
      : comprobantesApi.map(mapImputacionAFilaComprobante);

  // Facturas/NC desde imputaciones; adelantos desde adelantos_aplicados (info completa).
  const compsSinAdelantos = compsBase.filter((c) => !esDocumentoAdelanto(c));
  const adelantosDesdeApi =
    adelantosAplicadosApi.length > 0
      ? adelantosAplicadosApi.map(mapAdelantoAplicadoAFila)
      : compsBase.filter(esDocumentoAdelanto);

  const comprobantesTabla = [...compsSinAdelantos, ...adelantosDesdeApi];
  const adelantosAplicados = adelantosDesdeApi;
  const totalAdelantos = adelantosAplicados.reduce(
    (acc, ad) => acc + Math.abs(Number(ad.importe_a_pagar) || 0),
    0
  );

  const medioPago = mapMedioApiAWizard(
    mediosApi,
    medio,
    op,
    proveedorApi.nro_cuit ?? op.nro_cuit ?? op.cuit ?? ""
  );
  const cuentasOrigenDetalle = mapCuentasDesdeMedios(mediosApi, medio);
  if (medioPago.transferencia?.activo && cuentasOrigenDetalle.length) {
    medioPago.transferencia = {
      ...medioPago.transferencia,
      cuentasOrigen: cuentasOrigenDetalle.map((c) => ({
        key: c.key,
        codigoBanco: c.cuenta?.id_banco ?? "",
        id_bancos_cuentas: c.id_bancos_cuentas,
        importe: c.importe,
      })),
    };
  }

  const preview = {
    proveedor: {
      razon_social: proveedorApi.razon_social ?? op.razon_social,
      nro_cuit: proveedorApi.nro_cuit ?? op.nro_cuit,
      condicion_ganancias: proveedorApi.condicion_ganancias,
      codigo_ganancias_padron: proveedorApi.codigo_ganancias_padron,
    },
    resumen: {
      cantidad_comprobantes:
        resumenApi.cantidad_comprobantes ?? comprobantesTabla.length,
      total_facturas: Number(
        resumenApi.total_facturas ?? op.total_comprobantes ?? 0
      ),
      total_retenciones: Number(
        resumenApi.total_retenciones ??
          retencionesApi.total ??
          op.total_retenciones ??
          0
      ),
      total_adelantos: Number(
        resumenApi.total_adelantos ?? Math.round(totalAdelantos * 100) / 100
      ),
      total_a_pagar: Number(resumenApi.total_a_pagar ?? op.total_neto ?? 0),
      neto_imponible: resumenApi.neto_imponible ?? null,
      ...(Array.isArray(resumenApi.impuestos_factura)
        ? { impuestos_factura: resumenApi.impuestos_factura }
        : {}),
      ...(resumenApi.total_otros_impuestos_factura != null
        ? { total_otros_impuestos_factura: resumenApi.total_otros_impuestos_factura }
        : {}),
      ...(resumenApi.total_otros_impuestos_aportado != null
        ? { total_otros_impuestos_aportado: resumenApi.total_otros_impuestos_aportado }
        : {}),
    },
    ...(Array.isArray(data?.impuestos_factura)
      ? { impuestos_factura: data.impuestos_factura }
      : {}),
    ...(data?.total_otros_impuestos_factura != null
      ? { total_otros_impuestos_factura: data.total_otros_impuestos_factura }
      : {}),
    ...(data?.total_otros_impuestos_aportado != null
      ? { total_otros_impuestos_aportado: data.total_otros_impuestos_aportado }
      : {}),
    retenciones: {
      ...(retencionesApi.ganancias ? { ganancias: retencionesApi.ganancias } : {}),
      ...(retencionesApi.iva ? { iva: retencionesApi.iva } : {}),
      iibb: retencionesApi.iibb ?? [],
      total: Number(
        retencionesApi.total ?? resumenApi.total_retenciones ?? op.total_retenciones ?? 0
      ),
    },
    detalle: data?.detalle ?? null,
    advertencias: data?.advertencias ?? [],
  };

  const tieneRetencionesDetalladas =
    Boolean(retencionesApi.ganancias?.aplica) ||
    Boolean(retencionesApi.iva?.aplica) ||
    (retencionesApi.iibb ?? []).some((r) => Number(r.importe) > 0);

  return {
    ordenPago: op,
    anticipo: data?.anticipo ?? null,
    adelantosAplicados,
    medioPago,
    mediosPagoApi: Array.isArray(mediosApi)
      ? mediosApi
      : medio && Object.keys(medio).length
        ? [medio]
        : [],
    cuentasOrigenDetalle,
    comprobantesTabla,
    preview,
    detalle: data?.detalle ?? null,
    notificacionProveedor: data?.notificacion_proveedor ?? null,
    tieneRetencionesDetalladas,
  };
};

export const guardarBlobComoArchivo = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const mensajeNotificacionOp = (notif) => {
  if (!notif) return "";
  if (notif.mail_enviado) {
    return `Mail enviado a ${notif.destinatario || "el proveedor"}`;
  }
  if (notif.motivo_no_envio) {
    return `Mail no enviado: ${notif.motivo_no_envio}`;
  }
  return "";
};

export const esGeneracionMultiOp = (data) =>
  obtenerCantidadOrdenesGeneradas(data) > 1;

export const idPrimeraOrdenGenerada = (data) => {
  const opPlana = data?.orden_pago;
  if (opPlana?.id) return opPlana.id;
  const primera = data?.ordenes_pago?.[0];
  return primera?.orden_pago?.id ?? primera?.id ?? null;
};

export const mensajeOrdenGenerada = (data) => {
  const cantidad = obtenerCantidadOrdenesGeneradas(data);
  const estado =
    data?.orden_pago?.estado ??
    data?.ordenes_pago?.[0]?.orden_pago?.estado ??
    data?.ordenes_pago?.[0]?.estado;
  const estadoLabel =
    data?.orden_pago?.estado_label ??
    data?.ordenes_pago?.[0]?.orden_pago?.estado_label ??
    etiquetaEstadoOp(estado);
  const pendiente = esOpPendienteAprobacion(estado);

  if (cantidad > 1) {
    const ops = data?.ordenes_pago ?? [];
    const bancos = ops
      .map(
        (o) =>
          o.banco_nombre ??
          o.banco ??
          o.orden_pago?.banco_nombre ??
          o.medio_pago?.banco_nombre
      )
      .filter(Boolean);
    const listaBancos =
      bancos.length > 0 ? bancos.join(", ") : `${cantidad} bancos`;
    let mensaje = pendiente
      ? `Se crearon ${cantidad} órdenes de pago (una por banco: ${listaBancos}), ${estadoLabel?.toLowerCase?.() || "pendientes de aprobación"}`
      : `Se generaron ${cantidad} órdenes de pago (una por banco: ${listaBancos})`;

    const notif = data?.notificacion_proveedor;
    const detalleNotif = mensajeNotificacionOp(notif);
    if (detalleNotif) {
      mensaje += `. ${detalleNotif}`;
    } else if (notif?.nombre_adjunto) {
      mensaje += `. Documentación en ZIP: ${notif.nombre_adjunto}`;
    }

    return mensaje;
  }

  const nroOp =
    data?.orden_pago?.nro_op ?? data?.ordenes_pago?.[0]?.orden_pago?.nro_op;
  const notif =
    data?.notificacion_proveedor ??
    data?.ordenes_pago?.[0]?.notificacion_proveedor;
  let mensaje = nroOp
    ? pendiente
      ? `Orden de pago ${nroOp} creada · ${estadoLabel}`
      : `Orden de pago ${nroOp} generada correctamente`
    : pendiente
      ? `Orden de pago creada · ${estadoLabel}`
      : "Orden de pago generada correctamente";

  const detalleNotif = mensajeNotificacionOp(notif);
  if (detalleNotif) {
    mensaje += `. ${detalleNotif}`;
  }

  return mensaje;
};

/**
 * Arma el estado del wizard a partir del detalle de una OP (modo edición).
 * Solo se usa con estado pendiente_aprobacion.
 */
export const buildEstadoWizardDesdeDetalle = (detalleMapeado, dataRaw = null) => {
  const op = detalleMapeado?.ordenPago ?? dataRaw?.orden_pago ?? {};
  const previewBase = detalleMapeado?.preview ?? {};
  const proveedorApi =
    dataRaw?.proveedor ??
    previewBase?.proveedor ??
    {
      razon_social: op.razon_social,
      nro_cuit: op.nro_cuit,
    };
  const cuit =
    String(proveedorApi?.nro_cuit || op.nro_cuit || "")
      .replace(/\D/g, "") || "";

  const docs = (detalleMapeado?.comprobantesTabla ?? []).map((doc) => {
    const importe =
      Number(doc.importe_a_pagar ?? doc.importe) || 0;
    // Cupo de edición = libre del listado + lo ya imputado por esta OP.
    const saldoMax = saldoMaximoParaEdicionOp({
      ...doc,
      importe_a_pagar: importe,
    });
    const saldoTope = Number.isFinite(saldoMax) ? saldoMax : importe;
    // Mantener el importe de la OP; solo recortar si supera el cupo recalculado.
    const importeInicial = importeEsValido(importe, saldoTope, doc)
      ? importe
      : clampImporte(importe, saldoTope, doc);
    return {
      ...doc,
      seleccionable: true,
      puede_seleccionar: true,
      saldo_pendiente: saldoTope,
      importe_a_pagar: importeInicial,
      razon_social: proveedorApi.razon_social ?? op.razon_social,
      nro_cuit: cuit,
      id_proveedor: op.id_proveedor,
      id_empresa: op.id_empresa ?? doc.id_empresa,
      nombre_empresa: doc.nombre_empresa,
      cbu: op.cbu_destino ?? proveedorApi.cbu ?? doc.cbu,
      id_banco: proveedorApi.id_banco ?? doc.id_banco,
      banco_nombre:
        proveedorApi.banco_nombre ?? doc.banco_nombre ?? op.banco_nombre,
    };
  });

  const rowSelection = {};
  const importesAPagar = {};
  docs.forEach((doc) => {
    const id = idDocumentoPendiente(doc);
    if (!id) return;
    rowSelection[id] = true;
    importesAPagar[id] = doc.importe_a_pagar;
  });

  const saldoTotal = docs.reduce(
    (acc, d) => acc + (Number(d.saldo_pendiente) || 0),
    0
  );

  return {
    idOrdenPagoEdicion: op.id ?? null,
    nroOpEdicion: op.nro_op ?? null,
    modoEdicion: true,
    idEmpresaSeleccionada: op.id_empresa ?? null,
    nombreEmpresaSeleccionada: docs[0]?.nombre_empresa || "",
    cuitConsultado: cuit,
    rowSelection,
    importesAPagar,
    preview: previewBase,
    medioPago: detalleMapeado?.medioPago ?? null,
    resultado: {
      proveedores: [
        {
          id_proveedor: op.id_proveedor,
          razon_social: proveedorApi.razon_social ?? op.razon_social,
          nro_cuit: cuit,
          cbu: op.cbu_destino ?? proveedorApi.cbu,
          id_banco: proveedorApi.id_banco,
          banco_nombre: proveedorApi.banco_nombre,
          seleccionable: true,
          documentos: docs,
        },
      ],
      documentos: docs,
      resumen: {
        cantidad_proveedores: 1,
        cantidad_documentos: docs.length,
        saldo_total: Math.round(saldoTotal * 100) / 100,
      },
      filtros: { id_empresa: op.id_empresa },
      email: dataRaw?.notificacion_proveedor?.destinatario || "",
    },
  };
};

/* ------------------------------------------------------------------ *
 * Lotes de órdenes de pago
 * Una cabecera agrupa una OP por proveedor (los certificados, el CBU y
 * el archivo Interbanking siguen siendo por CUIT). El estado del lote lo
 * deriva el backend a partir de las OP que lo componen.
 * ------------------------------------------------------------------ */

export const ESTADO_LOTE = {
  PENDIENTE_APROBACION: "pendiente_aprobacion",
  PARCIALMENTE_APROBADO: "parcialmente_aprobado",
  APROBADO: "aprobado",
  ANULADO: "anulado",
};

export const ESTADO_LOTE_LABEL = {
  [ESTADO_LOTE.PENDIENTE_APROBACION]: "Pendiente de aprobación",
  [ESTADO_LOTE.PARCIALMENTE_APROBADO]: "Parcialmente aprobado",
  [ESTADO_LOTE.APROBADO]: "Aprobado",
  [ESTADO_LOTE.ANULADO]: "Anulado",
};

export const ESTADOS_LOTE_FILTRO = [
  {
    value: ESTADO_LOTE.PENDIENTE_APROBACION,
    label: ESTADO_LOTE_LABEL.pendiente_aprobacion,
  },
  {
    value: ESTADO_LOTE.PARCIALMENTE_APROBADO,
    label: ESTADO_LOTE_LABEL.parcialmente_aprobado,
  },
  { value: ESTADO_LOTE.APROBADO, label: ESTADO_LOTE_LABEL.aprobado },
  { value: ESTADO_LOTE.ANULADO, label: ESTADO_LOTE_LABEL.anulado },
];

/**
 * Filtros del listado unificado GET /orden-pago (filas mixtas lote + op).
 * `pendiente_aprobacion` incluye lotes pendientes/parciales y OP sueltas pendientes.
 */
export const ESTADOS_LISTADO_MIXTO_FILTRO = [
  {
    value: ESTADO_OP.PENDIENTE_APROBACION,
    label: "Pendiente de aprobación",
  },
  {
    value: ESTADO_LOTE.PARCIALMENTE_APROBADO,
    label: ESTADO_LOTE_LABEL.parcialmente_aprobado,
  },
  { value: ESTADO_OP.GENERADA, label: ESTADO_OP_LABEL.generada },
  { value: ESTADO_LOTE.APROBADO, label: ESTADO_LOTE_LABEL.aprobado },
  { value: ESTADO_OP.CONCILIADA, label: ESTADO_OP_LABEL.conciliada },
  { value: ESTADO_OP.ANULADA, label: ESTADO_OP_LABEL.anulada },
];

export const TIPO_FILA_LISTADO = {
  LOTE: "lote",
  OP: "op",
};

export const esFilaLoteListado = (row) =>
  String(row?.tipo || "")
    .trim()
    .toLowerCase() === TIPO_FILA_LISTADO.LOTE;

export const esFilaOpListado = (row) => !esFilaLoteListado(row);

/** Items de primer nivel del listado unificado. */
export const itemsListadoOrdenPago = (data) => {
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.ordenes_pago)) return data.ordenes_pago;
  return [];
};

const mapOpHijaDeLote = (op, lote) => {
  const proveedor = op?.proveedor ?? {};
  return {
    ...op,
    tipo: TIPO_FILA_LISTADO.OP,
    id: op?.id ?? op?.id_orden_pago ?? null,
    proveedor: {
      razon_social:
        proveedor.razon_social ?? op?.razon_social ?? "",
      nro_cuit:
        proveedor.nro_cuit ??
        proveedor.cuit ??
        op?.nro_cuit ??
        op?.cuit ??
        "",
    },
    lote: {
      id: lote?.id ?? null,
      nro_lote: lote?.nro_lote ?? null,
      estado: lote?.estado ?? null,
    },
    _enLote: true,
  };
};

/** Normaliza una fila del listado mixto (lote expandible u OP suelta). */
export const mapFilaListadoMixta = (item = {}) => {
  if (esFilaLoteListado(item)) {
    const proveedores = Array.isArray(item.proveedores) ? item.proveedores : [];
    const ordenesPago = (
      Array.isArray(item.ordenes_pago) ? item.ordenes_pago : []
    ).map((op) => mapOpHijaDeLote(op, item));
    const pagosNoProveedor = Array.isArray(item.pagos_no_proveedor)
      ? item.pagos_no_proveedor
      : [];
    const cantidadPagosNp = Number(
      item.cantidad_pagos_no_proveedor ?? pagosNoProveedor.length ?? 0
    );

    return {
      ...item,
      tipo: TIPO_FILA_LISTADO.LOTE,
      id: item.id ?? item.id_lote ?? null,
      nro_lote: item.nro_lote ?? item.nro ?? null,
      concepto: item.concepto ?? null,
      pagos_no_proveedor: pagosNoProveedor,
      cantidad_pagos_no_proveedor: cantidadPagosNp,
      proveedores: proveedores.map((prov) => ({
        cuit: String(prov?.cuit ?? prov?.nro_cuit ?? "").replace(/\D/g, ""),
        nro_cuit: String(prov?.nro_cuit ?? prov?.cuit ?? "").replace(/\D/g, ""),
        razon_social: prov?.razon_social ?? "",
        nro_op: prov?.nro_op ?? null,
      })),
      totales: normalizarTotalesLote(item.totales, []),
      cantidad_ordenes: Number(
        item.cantidad_ordenes ??
          item.cantidad_ordenes_pago ??
          item.cantidad_proveedores ??
          ordenesPago.length ??
          proveedores.length ??
          0
      ),
      ordenes_pago: ordenesPago,
    };
  }

  const proveedor = item?.proveedor ?? {};
  return {
    ...item,
    tipo: TIPO_FILA_LISTADO.OP,
    id: item.id ?? item.id_orden_pago ?? null,
    proveedor: {
      razon_social: proveedor.razon_social ?? item.razon_social ?? "",
      nro_cuit:
        proveedor.nro_cuit ??
        proveedor.cuit ??
        item.nro_cuit ??
        item.cuit ??
        "",
    },
    ordenes_pago: null,
    _enLote: false,
  };
};

/** Cantidad de pagos varios asociados a un lote (listado o detalle). */
export const cantidadPagosNoProveedorLote = (row) =>
  Number(
    row?.cantidad_pagos_no_proveedor ??
      (Array.isArray(row?.pagos_no_proveedor)
        ? row.pagos_no_proveedor.length
        : 0)
  ) || 0;

/** Lote solo con pagos varios (sin OP de proveedor) o marcado por concepto. */
export const esLoteConPagosVarios = (row) =>
  cantidadPagosNoProveedorLote(row) > 0 ||
  String(row?.concepto || "").toLowerCase() === "pagos_varios";

export const esLoteSoloPagosVarios = (row) => {
  if (!esFilaLoteListado(row) && row?.tipo !== TIPO_FILA_LISTADO.LOTE) {
    // detalle sin tipo: usar cantidad_ordenes / proveedores
    const cantOp = Number(
      row?.cantidad_ordenes ??
        row?.ordenes_pago?.length ??
        row?.proveedores?.length ??
        0
    );
    return cantOp === 0 && esLoteConPagosVarios(row);
  }
  const cantOp = Number(
    row?.cantidad_ordenes ?? row?.ordenes_pago?.length ?? 0
  );
  return cantOp === 0 && esLoteConPagosVarios(row);
};

export const idFilaListadoMixta = (row) => {
  const tipo = esFilaLoteListado(row) ? TIPO_FILA_LISTADO.LOTE : TIPO_FILA_LISTADO.OP;
  const id = row?.id ?? row?.id_orden_pago ?? row?.id_lote ?? "x";
  const hijo = row?._enLote ? `-lote${row?.lote?.id ?? ""}` : "";
  return `${tipo}-${id}${hijo}`;
};

export const normalizarEstadoLote = (estado) =>
  String(estado || "")
    .trim()
    .toLowerCase();

export const etiquetaEstadoLote = (estado, estadoLabel = null) => {
  if (estadoLabel) return String(estadoLabel);
  const e = normalizarEstadoLote(estado);
  return ESTADO_LOTE_LABEL[e] ?? (estado ? String(estado) : "—");
};

export const colorEstadoLote = (estado) => {
  const e = normalizarEstadoLote(estado);
  if (e === ESTADO_LOTE.APROBADO) return "success";
  if (e === ESTADO_LOTE.ANULADO) return "error";
  if (e === ESTADO_LOTE.PARCIALMENTE_APROBADO) return "info";
  if (e === ESTADO_LOTE.PENDIENTE_APROBACION) return "warning";
  return "default";
};

/** Quedan OP sin aprobar dentro del lote. */
export const esLoteAprobable = (estado) => {
  const e = normalizarEstadoLote(estado);
  return (
    e === ESTADO_LOTE.PENDIENTE_APROBACION ||
    e === ESTADO_LOTE.PARCIALMENTE_APROBADO
  );
};

/** Solo mientras el lote no esté aprobado ni anulado. */
export const esLoteAnulable = (estado) => esLoteAprobable(estado);

/**
 * True si la OP pertenece a un lote (no se aprueba ni anula por sí sola).
 */
export const opPerteneceALote = (orden) => {
  if (orden?._enLote) return true;
  const id =
    orden?.lote?.id ??
    orden?.lote?.id_lote ??
    orden?.id_lote ??
    null;
  return id != null && id !== "";
};

/**
 * Arma el body para POST /orden-pago/lotes.
 * `user`, `fecha_pago` y `observaciones` viajan en la raíz y cada item solo
 * repite lo que difiere, porque el backend hereda lo que falta.
 * `pagos_no_proveedor` viaja en la raíz (pagos varios / impuestos).
 */
export const buildLoteOrdenPagoBody = ({
  user,
  fechaPago,
  observaciones = "",
  proveedores = [],
  pagos_no_proveedor = [],
}) => {
  const pagosNp = Array.isArray(pagos_no_proveedor) ? pagos_no_proveedor : [];
  const proveedoresIn = Array.isArray(proveedores) ? proveedores : [];

  if (proveedoresIn.length === 0 && pagosNp.length === 0) {
    throw new Error("Seleccione al menos un proveedor o un pago varios");
  }
  if (!fechaPago) {
    throw new Error("Indique la fecha de pago");
  }

  const observacionesTrim = String(observaciones || "").trim();
  const vistos = new Set();

  const items = proveedoresIn.map(({ cuit, body }) => {
    const cuitNorm = String(cuit || "").replace(/\D/g, "");
    if (!cuitNorm) {
      throw new Error("Hay un proveedor sin CUIT válido en la selección");
    }
    if (vistos.has(cuitNorm)) {
      throw new Error(
        `El proveedor ${formatCuit(cuitNorm)} está repetido en el lote`
      );
    }
    vistos.add(cuitNorm);

    const {
      user: _user,
      fecha_pago: fechaItem,
      observaciones: obsItem,
      ...resto
    } = body ?? {};

    return {
      cuit: cuitNorm,
      ...resto,
      ...(fechaItem && fechaItem !== fechaPago
        ? { fecha_pago: fechaItem }
        : {}),
      ...(obsItem && obsItem !== observacionesTrim
        ? { observaciones: obsItem }
        : {}),
    };
  });

  return {
    user: String(user || "").trim(),
    fecha_pago: fechaPago,
    ...(observacionesTrim ? { observaciones: observacionesTrim } : {}),
    proveedores: items,
    ...(pagosNp.length ? { pagos_no_proveedor: pagosNp } : {}),
  };
};

/**
 * Body para POST /orden-pago/lotes/preview (validación de medios por proveedor).
 */
export const buildLotePreviewBody = ({
  fechaPago,
  observaciones = "",
  proveedoresItems = [],
  pagos_no_proveedor = [],
}) => {
  const observacionesTrim = String(observaciones || "").trim();
  const items = (proveedoresItems || []).map(({ cuit, ...resto }) => ({
    cuit: soloDigitos(cuit),
    ...resto,
  }));

  return {
    ...(fechaPago ? { fecha_pago: fechaPago } : {}),
    ...(observacionesTrim ? { observaciones: observacionesTrim } : {}),
    proveedores: items,
    ...(Array.isArray(pagos_no_proveedor) && pagos_no_proveedor.length
      ? { pagos_no_proveedor }
      : {}),
  };
};

/** Índice CUIT → item de preview de medios del lote. */
export const indexarPreviewMediosPorCuit = (previewMediosLote) => {
  const map = new Map();
  (previewMediosLote?.proveedores ?? []).forEach((prov, indice) => {
    const cuit = soloDigitos(prov?.cuit);
    if (!cuit) return;
    map.set(cuit, { ...prov, indice });
  });
  return map;
};

/** Resumen de medios de un proveedor desde el preview de lote. */
export const mediosResumenProveedorPreview = (previewMediosLote, cuit) => {
  const cuitDig = soloDigitos(cuit);
  const item = indexarPreviewMediosPorCuit(previewMediosLote).get(cuitDig);
  return item?.medios_resumen ?? null;
};

/** Cheques asignados a otros proveedores (para UI “ya utilizado”). */
export const chequesAsignadosOtrosProveedor = (
  chequesAsignados = {},
  cuitProv,
  proveedores = []
) => {
  const cuitDig = soloDigitos(cuitProv);
  return Object.values(chequesAsignados || {})
    .filter((ch) => {
      const asignado = soloDigitos(ch?.asignado_a?.cuit);
      return asignado && asignado !== cuitDig;
    })
    .map((ch) => {
      const cuitAsig = soloDigitos(ch?.asignado_a?.cuit);
      const prov = proveedores.find((p) => soloDigitos(p.cuit) === cuitAsig);
      return {
        id: ch.id,
        nro_cheque: ch.nro_cheque,
        importe: ch.importe,
        banco_emisor: ch.banco_emisor,
        recibido_de: ch.recibido_de,
        razon_social: prov?.razon_social || formatCuit(cuitAsig),
      };
    });
};

/** Mensaje amigable para error cheque_duplicado_lote. */
export const mensajeErrorChequeDuplicadoLote = (err) =>
  err?.codigo === "cheque_duplicado_lote"
    ? err.message ||
      "Un mismo cheque está asignado a más de un proveedor. Revisá la selección en el paso Medio de pago."
    : null;

/** Suma montos de pagos varios al resumen del preview (sin retenciones). */
export const mergePagosNoProveedorEnPreview = (
  preview,
  filasPagos = [],
  importesAPagar = {}
) => {
  if (!filasPagos.length) return preview;

  const redondear = (x) => Math.round(Number(x || 0) * 100) / 100;
  let totalPnp = 0;
  filasPagos.forEach((doc) => {
    const imp = importeSeleccionado(doc, importesAPagar);
    if (Number.isFinite(imp)) totalPnp += Math.abs(imp);
  });
  totalPnp = redondear(totalPnp);

  if (!preview) {
    return {
      ok: true,
      multi_proveedor: false,
      grupos: [],
      comprobantes: [],
      resumen: {
        cantidad_comprobantes: filasPagos.length,
        total_facturas: totalPnp,
        total_retenciones: 0,
        total_a_pagar: totalPnp,
      },
      resumen_global: {
        cantidad_comprobantes: filasPagos.length,
        total_facturas: totalPnp,
        total_retenciones: 0,
        total_a_pagar: totalPnp,
      },
      pagos_no_proveedor: filasPagos,
    };
  }

  const resumen = { ...(preview.resumen || {}) };
  const resumen_global = {
    ...(preview.resumen_global || preview.resumen || {}),
  };
  resumen.total_a_pagar = redondear(
    (Number(resumen.total_a_pagar) || 0) + totalPnp
  );
  resumen.total_facturas = redondear(
    (Number(resumen.total_facturas) || 0) + totalPnp
  );
  resumen.cantidad_comprobantes =
    (Number(resumen.cantidad_comprobantes) || 0) + filasPagos.length;
  resumen_global.total_a_pagar = redondear(
    (Number(resumen_global.total_a_pagar) || 0) + totalPnp
  );
  resumen_global.total_facturas = redondear(
    (Number(resumen_global.total_facturas) || 0) + totalPnp
  );
  resumen_global.cantidad_comprobantes =
    (Number(resumen_global.cantidad_comprobantes) || 0) + filasPagos.length;

  return {
    ...preview,
    resumen,
    resumen_global,
    pagos_no_proveedor: filasPagos,
  };
};

/** Referencia al lote desde una OP (listado o detalle). */
export const obtenerLoteDeOrdenPago = (orden) => {
  const lote = orden?.lote ?? null;
  const id = lote?.id ?? lote?.id_lote ?? orden?.id_lote ?? null;
  if (id == null) return null;
  return {
    id,
    nro_lote: lote?.nro_lote ?? lote?.nro ?? orden?.nro_lote ?? null,
    estado: lote?.estado ?? null,
    estado_label: lote?.estado_label ?? null,
    cantidad_ordenes:
      lote?.cantidad_ordenes ??
      lote?.cantidad_ordenes_pago ??
      lote?.cantidad_proveedores ??
      null,
  };
};

const numeroLote = (valor) => Number(valor) || 0;

/**
 * `totales_por_medio` llega agrupado por tipo de medio; se acepta tanto el
 * array del backend como un mapa { tipo: importe }.
 */
export const normalizarTotalesPorMedio = (raw) => {
  if (!raw) return [];

  const items = Array.isArray(raw)
    ? raw.map((item) => ({
        tipo: item?.tipo ?? item?.medio ?? item?.tipo_medio ?? "",
        importe: numeroLote(item?.importe ?? item?.total ?? item?.monto),
        cantidad: item?.cantidad ?? item?.lineas ?? item?.cantidad_lineas ?? null,
        extra: item,
      }))
    : Object.entries(raw).map(([tipo, valor]) =>
        valor && typeof valor === "object"
          ? {
              tipo,
              importe: numeroLote(valor.importe ?? valor.total ?? valor.monto),
              cantidad: valor.cantidad ?? valor.lineas ?? null,
              extra: valor,
            }
          : { tipo, importe: numeroLote(valor), cantidad: null, extra: null }
      );

  return items
    .filter((item) => item.tipo || item.importe)
    .map((item) => ({
      ...item,
      label: etiquetaTipoMedio(item.tipo, item.extra ?? {}),
    }))
    .sort((a, b) => b.importe - a.importe);
};

const totalesLoteDesdeProveedores = (proveedores = []) =>
  proveedores.reduce(
    (acc, prov) => ({
      comprobantes: acc.comprobantes + numeroLote(prov.totales?.comprobantes),
      retenciones: acc.retenciones + numeroLote(prov.totales?.retenciones),
      neto: acc.neto + numeroLote(prov.totales?.neto),
      cantidad_comprobantes:
        acc.cantidad_comprobantes + (prov.comprobantes?.length ?? 0),
    }),
    { comprobantes: 0, retenciones: 0, neto: 0, cantidad_comprobantes: 0 }
  );

const normalizarTotalesLote = (raw, proveedores = []) => {
  if (!raw) return totalesLoteDesdeProveedores(proveedores);
  return {
    comprobantes: numeroLote(
      raw.comprobantes ?? raw.total_comprobantes ?? raw.bruto
    ),
    retenciones: numeroLote(raw.retenciones ?? raw.total_retenciones),
    neto: numeroLote(raw.neto ?? raw.total_neto ?? raw.total_a_pagar),
    cantidad_comprobantes:
      raw.cantidad_comprobantes ??
      totalesLoteDesdeProveedores(proveedores).cantidad_comprobantes,
  };
};

const totalRetencionesProveedorLote = (retenciones) => {
  if (!retenciones) return 0;
  if (retenciones.total != null) return numeroLote(retenciones.total);
  return sumarImportesRetenciones(retenciones);
};

const mapProveedorLote = (item, index) => {
  const opRaw = item?.orden_pago ?? item ?? {};
  const proveedor = item?.proveedor ?? opRaw.proveedor ?? {};
  const comprobantes =
    item?.comprobantes ??
    opRaw.comprobantes ??
    item?.detalle?.comprobantes ??
    opRaw.detalle?.comprobantes ??
    [];
  const retenciones = item?.retenciones ?? opRaw.retenciones ?? null;
  const mediosPago =
    item?.medios_pago ??
    opRaw.medios_pago ??
    (opRaw.medio_pago
      ? [opRaw.medio_pago]
      : item?.medio_pago
        ? [item.medio_pago]
        : []);

  const totalesRaw = item?.totales ?? opRaw.totales ?? null;
  const netoFallback = comprobantes.reduce(
    (acc, comp) => acc + numeroLote(comp?.importe_a_pagar ?? comp?.importe),
    0
  );
  const retencionesTotal = totalRetencionesProveedorLote(retenciones);

  const ordenPago = {
    ...opRaw,
    id: opRaw.id ?? opRaw.id_orden_pago ?? item?.id ?? null,
    nro_op: opRaw.nro_op ?? item?.nro_op ?? null,
    estado: opRaw.estado ?? item?.estado ?? null,
    estado_label: opRaw.estado_label ?? item?.estado_label ?? null,
  };

  return {
    indice: index,
    cuit: String(
      item?.cuit ?? proveedor.nro_cuit ?? ordenPago.nro_cuit ?? ""
    ).replace(/\D/g, ""),
    razon_social:
      item?.razon_social ||
      proveedor.razon_social ||
      ordenPago.razon_social ||
      "",
    empresa: item?.empresa ?? ordenPago.empresa ?? null,
    ordenPago,
    comprobantes,
    retenciones,
    mediosPago,
    notificacion:
      item?.notificacion ??
      item?.notificacion_proveedor ??
      ordenPago.notificacion ??
      null,
    totales: {
      comprobantes: numeroLote(
        totalesRaw?.comprobantes ??
          totalesRaw?.total_comprobantes ??
          netoFallback
      ),
      retenciones: numeroLote(
        totalesRaw?.retenciones ??
          totalesRaw?.total_retenciones ??
          retencionesTotal
      ),
      neto: numeroLote(
        totalesRaw?.neto ??
          totalesRaw?.total_neto ??
          totalesRaw?.total_a_pagar ??
          ordenPago.total_neto ??
          netoFallback - retencionesTotal
      ),
    },
  };
};

/** Desglose de retenciones de un proveedor del lote, para la revisión. */
export const lineasRetencionesLote = (retenciones) => {
  if (!retenciones) return [];

  const lineas = [];
  const agregar = (key, label, importe) => {
    const valor = numeroLote(importe);
    if (valor > 0) lineas.push({ key, label, importe: valor });
  };

  agregar("ganancias", "Ganancias", retenciones.ganancias?.importe);
  agregar("iva", "IVA", retenciones.iva?.importe);
  (retenciones.iibb ?? []).forEach((item, index) => {
    const jurisdiccion =
      item?.provincia ?? item?.jurisdiccion ?? item?.descripcion ?? "";
    agregar(
      `iibb-${jurisdiccion || index}`,
      `IIBB${jurisdiccion ? ` ${jurisdiccion}` : ""}`,
      item?.importe
    );
  });

  return lineas;
};

const cabeceraLote = (lote = {}) => ({
  id: lote.id ?? lote.id_lote ?? null,
  nro_lote: lote.nro_lote ?? lote.nro ?? null,
  estado: lote.estado ?? null,
  estado_label: lote.estado_label ?? null,
  fecha: lote.fecha ?? lote.fecha_lote ?? null,
  fecha_pago: lote.fecha_pago ?? null,
  observaciones: lote.observaciones ?? "",
  user: lote.user ?? lote.usuario ?? null,
  created_at: lote.created_at ?? lote.fecha_alta ?? null,
  empresa: lote.empresa ?? null,
  motivo_anulacion: lote.motivo_anulacion ?? lote.motivo ?? null,
});

/** Adapta GET /orden-pago/lotes/:id a la pantalla de revisión. */
export const mapDetalleLoteResponse = (data) => {
  const loteRaw = data?.lote ?? data ?? {};
  const proveedoresRaw =
    loteRaw.proveedores ??
    data?.proveedores ??
    loteRaw.ordenes_pago ??
    data?.ordenes_pago ??
    [];

  const proveedores = (Array.isArray(proveedoresRaw) ? proveedoresRaw : []).map(
    mapProveedorLote
  );

  const pagosNoProveedor = Array.isArray(
    loteRaw.pagos_no_proveedor ?? data?.pagos_no_proveedor
  )
    ? loteRaw.pagos_no_proveedor ?? data?.pagos_no_proveedor
    : [];

  return {
    lote: {
      ...cabeceraLote(loteRaw),
      concepto: loteRaw.concepto ?? data?.concepto ?? null,
      cantidad_pagos_no_proveedor: Number(
        loteRaw.cantidad_pagos_no_proveedor ??
          data?.cantidad_pagos_no_proveedor ??
          pagosNoProveedor.length ??
          0
      ),
    },
    proveedores,
    pagos_no_proveedor: pagosNoProveedor,
    totales: normalizarTotalesLote(
      loteRaw.totales ?? data?.totales,
      proveedores
    ),
    totalesPorMedio: normalizarTotalesPorMedio(
      loteRaw.totales_por_medio ?? data?.totales_por_medio
    ),
  };
};

/** Fila de la grilla GET /orden-pago/lotes. */
export const mapFilaListadoLote = (lote = {}) => {
  const proveedores = Array.isArray(lote.proveedores) ? lote.proveedores : [];
  return {
    ...cabeceraLote(lote),
    cantidad_ordenes: Number(
      lote.cantidad_ordenes ??
        lote.cantidad_ordenes_pago ??
        lote.cantidad_proveedores ??
        proveedores.length ??
        0
    ),
    proveedores: proveedores.map((prov) => ({
      cuit: String(prov?.cuit ?? prov?.nro_cuit ?? "").replace(/\D/g, ""),
      razon_social: prov?.razon_social ?? "",
      nro_op: prov?.nro_op ?? prov?.orden_pago?.nro_op ?? null,
    })),
    totales: normalizarTotalesLote(lote.totales, []),
  };
};

export const idLoteGenerado = (data) =>
  data?.lote?.id ?? data?.id_lote ?? data?.lote?.id_lote ?? null;

export const nroLoteGenerado = (data) =>
  data?.lote?.nro_lote ?? data?.nro_lote ?? null;

export const mensajeLoteGenerado = (data) => {
  const nro = nroLoteGenerado(data);
  const cantidad = Number(
    data?.lote?.cantidad_ordenes ??
      data?.cantidad_ordenes ??
      data?.ordenes_pago?.length ??
      data?.lote?.proveedores?.length ??
      0
  );
  const cantidadPnp = Number(
    data?.lote?.cantidad_pagos_no_proveedor ??
      data?.cantidad_pagos_no_proveedor ??
      (Array.isArray(data?.lote?.pagos_no_proveedor)
        ? data.lote.pagos_no_proveedor.length
        : Array.isArray(data?.pagos_no_proveedor)
          ? data.pagos_no_proveedor.length
          : 0)
  );

  let detalle = "Quedó pendiente de aprobación.";
  if (cantidad && cantidadPnp) {
    detalle = `Incluye ${cantidad} orden${cantidad === 1 ? "" : "es"} de pago y ${cantidadPnp} pago${cantidadPnp === 1 ? "" : "s"} varios, pendientes de aprobación.`;
  } else if (cantidad) {
    detalle = `Incluye ${cantidad} orden${cantidad === 1 ? "" : "es"} de pago, una por proveedor, pendientes de aprobación.`;
  } else if (cantidadPnp) {
    detalle = `Incluye ${cantidadPnp} pago${cantidadPnp === 1 ? "" : "s"} varios, pendientes de aprobación.`;
  }

  return nro
    ? `Lote ${nro} creado. ${detalle}`
    : `Lote creado. ${detalle}`;
};

/**
 * Mensaje del error `proveedor_error`: el backend indica índice y CUIT del
 * proveedor que hizo fallar la creación (no se creó ninguna OP).
 */
export const mensajeErrorLote = (err, proveedores = []) => {
  const base =
    err?.message || "No se pudo generar el lote de órdenes de pago";
  const provError = err?.proveedorError;
  if (!provError) return base;

  const indice = Number(provError.indice ?? provError.index);
  const cuit = String(provError.cuit || "").replace(/\D/g, "");
  const grupo =
    proveedores.find(
      (p) => String(p?.cuit || "").replace(/\D/g, "") === cuit
    ) ?? (Number.isInteger(indice) ? proveedores[indice] : null);

  const nombre = grupo?.razon_social || provError.razon_social || "";
  const identidad = [nombre, cuit ? formatCuit(cuit) : null]
    .filter(Boolean)
    .join(" · ");

  const motivo = provError.msg || provError.message || provError.error || base;

  return identidad
    ? `${identidad}: ${motivo}\n\nNo se creó ninguna orden de pago: corregí ese proveedor y volvé a generar el lote.`
    : `${motivo}\n\nNo se creó ninguna orden de pago.`;
};

export const estadoComprobanteOp = (comp) => {
  if (comp?.tipo_pago_label) {
    return {
      label: comp.tipo_pago_label,
      color: colorEstadoTipoPago(comp.tipo_pago),
    };
  }

  const importe = Number(comp.importe_a_pagar);
  const saldo = Number(comp.saldo_pendiente);

  if (esComprobanteCredito(saldo, comp)) {
    if (importe > saldo + TOLERANCIA_IMPORTE) {
      return { label: "Aplicación parcial", color: "warning" };
    }
    return { label: "Aplicada", color: "info" };
  }
  if (importe < saldo - TOLERANCIA_IMPORTE) {
    return { label: "Pago parcial", color: "warning" };
  }
  return { label: "Incluida", color: "success" };
};
